import * as R from 'ramda';
import * as P from 'plow-js';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature dispatch-planner
import * as C from './constants';
import {
  addTelItems,
  sortTelEvents,
  getItemsToRemove,
  concatArrayItemsProp,
  filterPickedDroppedItems,
  getAvailableContainersForDrop,
  indexObjectArrayPropByPropName,
} from '../dispatch-planner-v2/shared/helpers';
//////////////////////////////////////////////////

// COMMON
export const getLoadName = (load, newNameText = '') => R.pathOr(
  newNameText,
  ['primaryReference', 'value'],
  load,
);

export const mergeArrayItemsProp = (arr = [], prop) => (
  R.reduce(
    R.mergeRight,
    {},
    R.map((entity = {}) => entity[prop], arr),
  )
);

export const getCloItemsInTelByEventType = (tel, eventType) => R.compose(
  R.filter(G.isLoadTypeClo),
  (events) => concatArrayItemsProp(events, 'items'),
  R.filter((event) => R.propEq(eventType, 'eventType', event)),
  R.values,
)(tel[GC.FIELD_LOAD_STOPS]);

const getTelCloItemsByType = (tel, type) => R.compose(
  R.filter(G.isLoadTypeClo),
  R.reduce((acc, stop) => R.concat(acc, stop.items), []),
  R.filter(R.propEq(type, GC.FIELD_EVENT_TYPE)),
  R.values,
  R.prop(GC.FIELD_LOAD_STOPS),
)(tel);

const getTerminalCloItemsByType = (tels, telGuid, templateId, type) => {
  let telsToUse = tels;

  if (R.equals(type, GC.EVENT_TYPE_DROP)) {
    telsToUse = R.filter(({ guid }) => G.notEquals(guid, telGuid), tels);
  }

  return R.compose(
    R.filter(G.isLoadTypeClo),
    R.reduce((acc, { items }) => R.concat(acc, items), []),
    R.filter((stop) => G.isAllTrue(
      R.propEq(type, GC.FIELD_EVENT_TYPE, stop),
      G.isStopTypeTerminal(G.getPropFromObject(GC.FIELD_STOP_TYPE, stop)),
      R.pathEq(templateId, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_ID], stop),
    )),
    R.reduce((acc, { events }) => R.concat(acc, R.values(events)), []),
  )(telsToUse);
};

export const getAvailableTerminalPickupClos = (tel, tels, templateId) => {
  const telGuid = G.getGuidFromObject(tel);

  const terminalDropedItems = getTerminalCloItemsByType(tels, telGuid, templateId, GC.EVENT_TYPE_DROP);
  const terminalPickedItems = getTerminalCloItemsByType(tels, telGuid, templateId, GC.EVENT_TYPE_PICKUP);
  const availableTerminalItems = filterPickedDroppedItems(terminalDropedItems, terminalPickedItems);

  const pickedTelItems = getTelCloItemsByType(tel, GC.EVENT_TYPE_PICKUP);
  const droppedTelItems = getTelCloItemsByType(tel, GC.EVENT_TYPE_DROP);
  const availableTelItems = filterPickedDroppedItems(droppedTelItems, pickedTelItems);

  const availableItems = R.compose(
    R.values,
    R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
    R.concat(availableTerminalItems),
  )(availableTelItems);

  if (G.isNilOrEmpty(availableItems)) return null;

  const clos = {};

  availableItems.forEach((item) => {
    const cloGuid = R.path([GC.FIELD_LOAD_GUID], item);

    if (R.not(R.has(cloGuid, clos))) clos[cloGuid] = [];

    clos[cloGuid].push(item);
  });

  return clos;
};

export const getAvailableDropClosWithItems = (tel) => {
  const droppedItems = getCloItemsInTelByEventType(tel, GC.EVENT_TYPE_DROP);
  const pickedItems = getCloItemsInTelByEventType(tel, GC.EVENT_TYPE_PICKUP);
  const availableItems = filterPickedDroppedItems(pickedItems, droppedItems);

  if (G.isNilOrEmpty(availableItems)) return null;

  const clos = {};

  availableItems.forEach((item) => {
    const cloGuid = R.path([GC.FIELD_LOAD_GUID], item);

    if (R.not(R.has(cloGuid, clos))) clos[cloGuid] = [];

    clos[cloGuid].push(item);
  });

  return clos;
};

export const getAvailableItemsForPickup = (stop, items) => (
  R.filter(
    (item) => G.isNilOrEmpty(
      R.find(
        (id) => R.equals(R.prop('itemInternalId', item), id),
        stop.itemIds,
      ),
    ),
    items,
  )
);

export const getTelItemsForDrop = (props) => {
  const { tel, items } = props;

  const getFilteredEvents = (eventType) => R.filter(
    (event) => R.and(
      R.not(R.path([GC.FIELD_CLO_EVENT], event)),
      R.propEq(eventType, GC.FIELD_EVENT_TYPE, event),
    ),
    R.values(R.pathOr([], [GC.FIELD_LOAD_STOPS], tel)),
  );

  const pickups = getFilteredEvents(GC.EVENT_TYPE_PICKUP);
  const drops = getFilteredEvents(GC.EVENT_TYPE_DROP);

  const getEventItemIds = (events) => R.reduce(
    (acc, { itemIds }) => R.concat(acc, itemIds), [], events,
  );

  const pickedItemIds = getEventItemIds(pickups);
  const droppedItemIds = getEventItemIds(drops);

  const itemIds = R.difference(pickedItemIds, droppedItemIds);

  return R.compose(
    R.values,
    R.pick(itemIds),
    R.filter(({ loadType }) => R.not(G.isLoadTypeClo(loadType))),
  )(items);
};

const getItemsByEventTypeAndLoadGuid = (tels, dropStop, eventType) => R.compose(
  R.reduce((acc, stop) => R.concat(acc, stop.itemIds), []),
  R.filter((stop) => R.and(
    R.eqProps(GC.FIELD_LOAD_GUID, stop, dropStop),
    R.equals(eventType, stop.eventType),
  )),
  R.values,
  R.reduce((acc, { events }) => R.mergeRight(acc, events), {}),
  R.values,
)(tels);

export const getCloItemsForDrop = (props) => {
  const dropStop = props.stop;
  const tels = R.path(['currentRoute', GC.SYSTEM_LIST_TELS], props);
  const pickedItems = getItemsByEventTypeAndLoadGuid(tels, dropStop, GC.EVENT_TYPE_PICKUP);
  const droppedItems = getItemsByEventTypeAndLoadGuid(tels, dropStop, GC.EVENT_TYPE_DROP);
  const itemIds = R.difference(pickedItems, droppedItems);

  return R.map((id) => R.path(['items', id], props), itemIds);
};

export const getTerminalInitialDates = ({ dropTels, pickupTels } = {}, tels = {}) => {
  const currentDateTime = G.getCurrentDateWithFormat(GC.DEFAULT_DATE_TIME_FORMAT);

  let dates = {
    eventLateDate: currentDateTime,
    eventEarlyDate: currentDateTime,
  };

  if (G.isNotNilAndNotEmpty(dropTels) && G.isNotNilAndNotEmpty(tels[R.head(dropTels)])) {
    const lastPickup = R.compose(
      R.last,
      R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
      R.filter((event) => R.and(
        R.or(event.cloEvent, R.equals(R.prop('stopType', event), GC.STOP_TYPE_TERMINAL)),
        R.equals(event.eventType, GC.EVENT_TYPE_PICKUP),
      )),
      R.values,
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tels[R.head(dropTels)]);

    if (G.isNotNilAndNotEmpty(lastPickup)) {
      const lastEventDate = R.or(G.getStopLateDate(lastPickup), G.getStopEarlyDate(lastPickup));

      dates = {
        eventLateDate: G.addDateTimeWithFormat(lastEventDate, 8),
        eventEarlyDate: G.addDateTimeWithFormat(lastEventDate, 8),
      };
    }
  }

  return dates;
};

export const sortTelEventsByDate = (events) => R.compose(
  R.indexBy(R.prop(GC.FIELD_GUID)),
  G.mapIndexed((item, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), item)),
  R.sort((prevEvent, event) => (
    G.getDateRange(G.getStopEarlyDate(event), G.getStopEarlyDate(prevEvent))
  )),
  R.values,
)(events);

export const isEventItemsPickedUp = (event, tel) => R.compose(
  R.equals(R.prop(GC.FIELD_STOP_ITEM_IDS, event)),
  R.filter((id) => G.isNotNil(R.find(
    R.equals(id),
    R.prop(GC.FIELD_STOP_ITEM_IDS, event),
  ))),
  R.reduce((acc, stop) => R.concat(acc, R.prop(GC.FIELD_STOP_ITEM_IDS, stop)), []),
  R.filter((stop) => G.isStopPickup(stop)),
  R.values,
  R.prop(GC.FIELD_LOAD_STOPS),
)(tel);

export const isEventItemsDropped = (event, tel) => R.compose(
  R.equals(R.prop(GC.FIELD_STOP_ITEM_IDS, event)),
  R.filter((id) => G.isNotNil(R.find(
    R.equals(id),
    R.prop(GC.FIELD_STOP_ITEM_IDS, event),
  ))),
  R.reduce((acc, stop) => R.concat(acc, R.prop(GC.FIELD_STOP_ITEM_IDS, stop)), []),
  R.filter((stop) => G.isStopDrop(stop)),
  R.values,
  R.prop(GC.FIELD_LOAD_STOPS),
)(tel);

export const isPlannerEmpty = (currentRoute) => {
  const { tels, deletedTelGuids } = currentRoute;

  const telsArray = R.values(tels);

  if (G.isNotNilAndNotEmpty(deletedTelGuids)) return false;

  if (R.isEmpty(telsArray)) return true;

  return R.and(
    R.equals(R.length(telsArray), 1),
    R.isEmpty(R.values(R.prop(GC.FIELD_LOAD_STOPS, R.head(telsArray)))),
  );
};

// COMMON

// SAGA
export const getInitialReportFromResponse = (reports, guid, routeGuid) => {
  const defaultReport = R.or(R.find(R.pathEq(true, ['defaultReport']))(reports), reports[0]);

  return {
    empty: defaultReport,
    report: defaultReport,
    withSearchCriteria: G.ifElse(
      G.isNilOrEmpty(routeGuid),
      false,
      R.assoc(
        'searchCriteria',
        R.of(Array, {
          dataType: 'string',
          operation: 'equal',
          stringValue: routeGuid,
          propertyName: 'route.guid',
        }),
        defaultReport,
      ),
    ),
  };
};

export const getReportFromReportsResponse = (reports) => R.or(
  R.find(R.pathEq(true, ['defaultReport']))(reports),
  R.head(reports),
);

export const getItemsByIds = (ids, items, isNewEvent) => {
  const commonFields = ['isNew', 'selected', 'telGuid', 'cloGuid', GC.FIELD_BRANCH_GUID];
  const omitFields = G.ifElse(
    isNewEvent,
    ['eventGuid', ...commonFields],
    commonFields,
  );

  return R.map((id) => {
    const item = items[id];

    if (G.isTrue(R.path(['isNew'], item))) {
      return R.omit([GC.FIELD_GUID, ...omitFields], item);
    }

    return R.omit(omitFields, item);
  }, ids);
};

export const getEventWithItems = (event, currentRouteItems) => R.assoc(
  'items',
  getItemsByIds(event.itemIds, currentRouteItems, R.prop('isNew', event)),
  event,
);

export const transformEventsForRequest = (tel, currentRouteItems) => R.assoc(
  GC.FIELD_LOAD_STOPS,
  R.compose(
    G.mapIndexed(
      (stop, i) => {
        const event = {
          ...G.formatStopDateTimeValues(G.mapObjectEmptyStringFieldsToNull(stop)),
          [GC.FIELD_TEL_EVENT_INDEX]: R.inc(i),
          [GC.FIELD_STOP_DROPPED_CONTAINERS]: G.mapArrayObjectEmptyStringFieldsToNull(
            G.getPropFromObject(GC.FIELD_STOP_DROPPED_CONTAINERS, stop),
          ),
          [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: G.mapArrayObjectEmptyStringFieldsToNull(
            G.getPropFromObject(GC.FIELD_STOP_PICKED_UP_CONTAINERS, stop),
          ),
        };

        const pathToOperationHour = [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_OPERATION_HOUR];

        const eventWithOperationHour = R.assocPath(
          pathToOperationHour,
          R.compose(
            G.convertOperationHoursToDefaultFormat,
            R.pathOr([], pathToOperationHour),
          )(event),
          event,
        );

        let eventWithItems = getEventWithItems(eventWithOperationHour, currentRouteItems);

        if (G.isTrue(R.prop('isNew', tel))) {
          eventWithItems = R.omit([GC.FIELD_TEL_GUID], eventWithItems);
        }

        if (G.isTrue(R.prop('isNew', eventWithItems))) {
          return R.omit(['isNew', GC.FIELD_GUID], eventWithItems);
        }

        return eventWithItems;
      }),
      R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    )(R.values(tel[GC.FIELD_LOAD_STOPS])),
  tel,
);

export const transformTelRateForRequest = (tel, initRate) => {
  const rate = R.prop(GC.SYSTEM_OBJECT_RATE, tel);

  if (R.equals(rate, initRate)) return R.omit([GC.FIELD_RATE], tel);

  if (G.isNilOrEmpty(rate)) return tel;

  if (G.isNotNilAndNotEmpty(R.prop('carrierAssignment', rate))) {
    return G.renameKeys({ rate: 'carrierRate' }, tel);
  }

  if (G.isNilOrEmpty(R.prop('fleetAssignment', rate))) {
    return R.assoc(
      'fleetRate',
      R.assoc(
        'fleetAssignment',
        R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, rate),
        rate,
      ),
      R.omit('rate', tel),
    );
  }

  return G.renameKeys({ rate: 'fleetRate' }, tel);
};

const getPrimaryReferenceValue = (telWithRate, telPrimaryRefAuto, primaryReferenceTypeGuid) => {
  if (R.and(G.isTrue(telPrimaryRefAuto), G.isNilOrEmpty(primaryReferenceTypeGuid))) return null;

  return R.path([GC.FIELD_PRIMARY_REFERENCE, GC.FIELD_VALUE], telWithRate);
};

export const transformTelsForRequest = (
  route,
  currentRouteItems,
  telPrimaryRefAuto,
  initialRoute,
) => R.assoc(
  GC.SYSTEM_LIST_TELS,
  R.compose(
    R.map((tel) => {
      const telWithEvents = transformEventsForRequest(tel, currentRouteItems);

      const telWithRate = transformTelRateForRequest(
        telWithEvents,
        R.path([GC.SYSTEM_LIST_TELS, G.getGuidFromObject(tel), GC.FIELD_RATE], initialRoute),
      );

      const primaryReferenceTypeGuid = R.path([GC.FIELD_PRIMARY_REFERENCE, GC.FIELD_REFERENCE_TYPE_GUID], telWithRate);

      const telWithPrimaryReference = R.mergeRight(
        telWithRate,
        {
          primaryReferenceTypeGuid,
          [C.TEL_INDEX_IN_ROUTE]: tel.order,
          primaryReferenceValue: getPrimaryReferenceValue(telWithRate, telPrimaryRefAuto, primaryReferenceTypeGuid),
        },
      );

      if (G.isTrue(R.prop('isNew', telWithPrimaryReference))) {
        return R.omit(['isNew', GC.FIELD_GUID], telWithPrimaryReference);
      }

      return telWithPrimaryReference;
    }),
    R.filter((tel) => G.isNotNilAndNotEmpty(R.values(tel[GC.FIELD_LOAD_STOPS]))),
  )(R.values(route.tels)),
  route,
);

export const setTelEventItemsForRateAssignRequest = (telEvents, currentRouteItems) => R.map(
  (event) => getEventWithItems(event, currentRouteItems),
  telEvents,
);

export const transformCurrentRouteForRequest = (
  route,
  currentRouteItems,
  telPrimaryRefAuto,
  initialRoute,
) => (
  R.omit(
    ['isNew', GC.SYSTEM_LIST_CLOS],
    transformTelsForRequest(route, currentRouteItems, telPrimaryRefAuto, initialRoute),
  )
);

const keysMap = {
  distance: GC.FIELD_DISTANCE_SYSTEM,
  unit: GC.FIELD_DISTANCE_SYSTEM_UOM,
};

const emptyDistance = {
  [GC.FIELD_DISTANCE_SYSTEM]: null,
  [GC.FIELD_DISTANCE_SYSTEM_UOM]: null,
};

export const mapLoadEventsWithDistances = (
  load,
  loadType,
  distanceRes,
) => {
  const distancesArr = R.compose(
    R.append(emptyDistance),
    R.map((item) => G.renameKeys(keysMap, item)),
    R.map((item) => R.pick(['distance', 'unit'], item)),
    R.prop('stopResults'),
  )(distanceRes);

  const events = R.compose(
    R.sortBy(R.prop(G.getEventIndexPropByLoadType(loadType))),
    R.values(),
    R.prop(GC.FIELD_LOAD_STOPS),
  )(load);

  const zippedEvents = R.zipWith((event, distance) => {
    const distanceToNextStop = R.prop(G.getDistancePropByLoadType(loadType), event);
    const newDistanceToNextStop = R.mergeRight(distanceToNextStop, distance);

    return R.assoc(G.getDistancePropByLoadType(loadType), newDistanceToNextStop, event);
  }, events, distancesArr);

  return R.indexBy(R.prop(GC.FIELD_GUID), zippedEvents);
};

export const mapRouteItems = (tels = []) => {
  const cloPrimaryReferenceValues = R.reduce(
    (acc, tel) => R.mergeRight(acc, tel.cloPrimaryReferenceValues),
    {},
    tels,
  );
  const items = R.compose(
    R.map((item) => {
      const updatedItem = R.mergeRight(item, {
        isNew: false,
        selected: true,
      });

      if (G.isLoadTypeClo(updatedItem)) {
        return R.assoc(
          C.CLO_NAME,
          R.prop(R.prop(GC.FIELD_LOAD_GUID, updatedItem), cloPrimaryReferenceValues),
          updatedItem,
        );
      }

      return updatedItem;
    }),
    R.uniqBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
  )(concatArrayItemsProp(concatArrayItemsProp(tels, GC.FIELD_LOAD_STOPS), GC.FIELD_LOAD_ITEMS));

  return R.indexBy(
    R.prop(GC.FIELD_ITEM_INTERNAL_ID),
    items,
  );
};

const getCloName = (tel, entity) => R.path(
  ['cloPrimaryReferenceValues', R.prop(GC.FIELD_LOAD_GUID, entity)],
  tel,
);

const mapTelEventsWithCloPrimaryRef = (tel) => R.map(
  (event) => {
    const stopWithMappedContainers = {
      ...event,
      [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.map(
        (container) => R.assoc(C.CLO_NAME, getCloName(tel, container), container),
        R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, event),
      ),
      [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.map(
        (container) => R.assoc(C.CLO_NAME, getCloName(tel, container), container),
        R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS, event),
      ),
    };

    if (G.isLoadTypeClo(event)) {
      const cloName = getCloName(tel, event);
      const assocClo = R.assoc(C.CLO_NAME, cloName);

      return R.mergeRight(
        stopWithMappedContainers,
        {
          [C.CLO_NAME]: cloName,
          [GC.FIELD_LOAD_ITEMS]: R.map(assocClo, R.prop(GC.FIELD_LOAD_ITEMS, stopWithMappedContainers)),
        },
      );
    }

    return stopWithMappedContainers;
  },
  tel.events,
);

const extendRouteTel = (tel, index) => {
  const { order, references, primaryReferenceValue } = tel;

  return R.mergeRight(
    tel,
    {
      [GC.FIELD_LOAD_STOPS]: mapTelEventsWithCloPrimaryRef(tel),
      [GC.FIELD_ORDER]: G.ifElse(G.isNumber(order), order, index),
      [GC.FIELD_PRIMARY_REFERENCE]: R.pathOr(
        R.find(R.prop(GC.FIELD_PRIMARY), R.or(references, [])),
        [GC.FIELD_PRIMARY_REFERENCE],
        tel,
      ),
      loadName: G.ifElse(
        G.isNotNilAndNotEmpty(R.prop(GC.FIELD_PRIMARY_REFERENCE_VALUE, tel)),
        primaryReferenceValue,
        getLoadName(tel),
      ),
    },
  );
};

export const convertTelResponse = ({ tel, indexAdditional = 0 }) => {
  const tels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((tel, index) => indexObjectArrayPropByPropName(
      GC.FIELD_GUID,
      GC.FIELD_LOAD_STOPS,
      extendRouteTel(tel, R.add(index, indexAdditional)),
    )),
    R.of(Array),
  )(tel);

  return { tels };
};

export const getCloEventGuidsFromCurrentRouteWithoutSelected = (currentRoute, guids) => R.compose(
  R.without(guids),
  R.map(G.getGuidFromObject),
  R.filter((stop) => G.isTrue(R.path([GC.FIELD_CLO_EVENT], stop))),
  R.reduce((acc, tel) => R.concat(acc, R.values(R.prop(GC.FIELD_LOAD_STOPS, tel))), []),
  R.values,
  R.prop(GC.SYSTEM_LIST_TELS),
)(currentRoute);

export const getEventGuidsFromListAndRoute = (events, currentRoute) => R.compose(
  R.uniq,
  R.concat(R.map(R.prop(GC.FIELD_GUID), events)),
  R.map(R.prop(GC.FIELD_GUID)),
  R.filter((stop) => G.isTrue(R.path([GC.FIELD_CLO_EVENT], stop))),
  R.reduce((acc, tel) => R.concat(acc, R.values(R.prop(GC.FIELD_LOAD_STOPS, tel))), []),
  R.values,
  R.prop(GC.SYSTEM_LIST_TELS),
)(currentRoute);

export const getRateAndEventGuidsFromCurrentRoute = ({ tels }) => {
  const eventGuids = R.compose(
    R.map(G.getGuidFromObject),
    R.filter((event) => R.not(R.prop('isNew', event))),
    R.reduce((acc, tel) => R.concat(acc, R.values(R.prop(GC.FIELD_LOAD_STOPS, tel))), []),
    R.values,
  )(tels);

  const rateGuids = R.compose(
    R.map(({ rate }) => G.getGuidFromObject(rate)),
    R.filter((tel) => R.and(
      G.isNotNilAndNotEmpty(R.path([GC.SYSTEM_OBJECT_RATE, GC.FIELD_GUID], tel)),
      R.not(R.path([GC.SYSTEM_OBJECT_RATE, 'isNew'], tel)),
    )),
    R.values,
  )(tels);

  return { rateGuids, eventGuids };
};
// SAGA

// REDUCER
export const setSelectedPropIfExist = (tels, data) => {
  if (G.isNilOrEmpty(tels)) return tels;

  return tels.map((tel) => R.assoc(
    'selected',
    G.ifElse(R.includes(R.prop(GC.FIELD_GUID, tel), data), true, G.getPropFromObject('selected', tel)),
    tel,
  ));
};

export const selectTelItems = (tels, ids) => (
  R.map((tel) => (
    P.$set(
      'selected',
      G.isNotNilAndNotEmpty(R.find(
        (id) => R.equals(R.prop('guid', tel), id),
        ids,
      )),
      tel,
    )
  ), tels)
);

export const selectItem = (list, id, value = false) => (
  R.map((item) => (
    P.$set(
      'selected',
      G.ifElse(
        R.equals(R.prop('guid', item), id),
        R.or(value, R.not(R.prop('selected', item))),
        R.prop('selected', item),
      ),
      item,
    )
  ), list)
);

export const filterCurrentRouteItems = (items = {}, eventItems = []) => R.omit(
  R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), eventItems),
  items,
);

export const resetCloListToInitialRoute = (currentRoute, cloList) => (
  R.map(
    (clo) => R.assoc(
      'selected',
      G.isNotNilAndNotEmpty(R.path(['clos', R.prop('guid', clo)], currentRoute)),
      clo,
    ),
    cloList,
  )
);

export const createNewRouteTel = (options) => (
  R.mergeRight(
    {
      rate: null,
      events: {},
      isNew: true,
      guid: G.generateGuid(),
      loadType: GC.LOAD_TYPE_TEL,
    },
    options,
  )
);

const getLocationType = ({ locationType }) => {
  if (G.isNilOrEmpty(locationType)) return null;

  if (G.isString(locationType)) return locationType;

  return R.omit([GC.FIELD_GUID], locationType);
};

export const omitLocationFields = (location) => R.omit(
  R.concat(GC.GROUPED_FIELDS.EVENT_DATES_ARR, GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR),
  R.mergeRight(location, {
    [GC.FIELD_LOCATION_TYPE]: getLocationType(location),
    [GC.FIELD_CONTACTS]: R.map(
      R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR),
      R.pathOr([], [GC.FIELD_CONTACTS], location),
    ),
    [GC.FIELD_OPERATION_HOUR]: R.map(
      R.omit([GC.FIELD_GUID]),
      R.pathOr([], [GC.FIELD_OPERATION_HOUR], location),
    ),
  }),
);

export const createNewStopOnRouteTel = (options) => ({
  items: [],
  itemIds: [],
  isNew: true,
  cloGuid: null,
  comments: null,
  cloEvent: false,
  completed: false,
  trailerGuids: [],
  drop: options.drop,
  cloEventIndex: null,
  appointmentDate: null,
  pickup: options.pickup,
  guid: G.generateGuid(),
  eventCompleteDate: null,
  appointmentNumber: null,
  telGuid: options.telGuid,
  appointmentLateTime: null,
  appointmentEarlyTime: null,
  cloDistanceToNextStop: null,
  telDistanceToNextStop: null,
  eventType: options.eventType,
  telEventIndex: options.telEventIndex,
  eventLateDate: options.eventLateDate,
  eventEarlyDate: options.eventEarlyDate,
  location: omitLocationFields(options.location),
  stopType: R.or(options.stopType, GC.STOP_TYPE_INDEPENDENT),
  // trailers
  [GC.FIELD_STOP_DROPPED_TRAILERS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILERS]: [],
  [GC.FIELD_STOP_DROPPED_TRAILER_GUIDS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS]: [],
  // containers
  [GC.FIELD_STOP_DROPPED_CONTAINERS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: [],
  [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: [],
  [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: [],
});

export const createNewTerminalOnRouteTel = (options) => {
  const newStop = createNewStopOnRouteTel(options);
  const guid = G.generateGuid();

  return {
    ...newStop,
    guid,
    stopId: options.stopId,
    stopType: R.or(options.stopType, GC.STOP_TYPE_TERMINAL),
  };
};

export const getNextTelEventIndex = (telGuid, state) => {
  const length = R.length(
    R.values(R.pathOr({}, ['currentRoute', 'tels', telGuid, GC.FIELD_LOAD_STOPS], state),
  ));

  return R.add(length, 1);
};

export const getLocationByStopType = (location) => {
  if (R.path(['isNew'], location)) {
    return omitLocationFields(location);
  }

  return R.omit(GC.GROUPED_FIELDS.EVENT_DATES_ARR, location);
};

export const updateEventByStopType = (event) => {
  if (R.pathEq(GC.STOP_TYPE_TERMINAL, [GC.FIELD_STOP_TYPE], event)) {
    return R.assoc(
      'location',
      R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR, event.location),
      event,
    );
  }

  return event;
};

export const generateDataForNewTelStopFromStopForm = (data, state) => {
  const { location, loadGuid, eventType } = data;

  const telGuid = loadGuid;

  const drop = G.isEventTypeDrop(eventType);
  const pickup = G.isEventTypePickup(eventType);
  const telEventIndex = getNextTelEventIndex(telGuid, state);
  const eventLateDate = R.propOr(null, GC.FIELD_LOAD_EVENT_LATE_DATE, location);
  const eventEarlyDate = R.propOr(null, GC.FIELD_LOAD_EVENT_EARLY_DATE, location);

  return {
    drop,
    pickup,
    telGuid,
    location,
    eventType,
    telEventIndex,
    eventLateDate,
    eventEarlyDate,
  };
};

export const createNewCloStopOnRouteTel = (options) => ({
  isNew: true,
  [GC.FIELD_STOP_ITEMS]: [],
  [GC.FIELD_COMMENTS]: null,
  [GC.FIELD_CLO_EVENT]: true,
  [GC.FIELD_STOP_TYPE]: null,
  [GC.FIELD_COMPLETED]: false,
  [GC.FIELD_STOP_ITEM_IDS]: [],
  [GC.FIELD_TRAILER_GUIDS]: [],
  [GC.FIELD_DISTANCE_CLO]: null,
  [GC.FIELD_DISTANCE_TEL]: null,
  [GC.FIELD_CLO_EVENT_INDEX]: null,
  [GC.FIELD_GUID]: G.generateGuid(),
  [GC.FIELD_EVENT_COMPLETE_DATE]: null,
  [GC.FIELD_LOAD_TYPE]: GC.LOAD_TYPE_CLO,
  [GC.FIELD_LOAD_APPOINTMENT_DATE]: null,
  [GC.FIELD_LOAD_APPOINTMENT_NUMBER]: null,
  [GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]: null,
  [GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME]: null,
  [GC.FIELD_LOCATION]: R.prop(GC.FIELD_LOCATION, options),
  [GC.FIELD_CLO_GUID]: R.prop(GC.FIELD_CLO_GUID, options),
  [GC.FIELD_TEL_GUID]: R.prop(GC.FIELD_TEL_GUID, options),
  [GC.FIELD_LOAD_GUID]: R.prop(GC.FIELD_CLO_GUID, options),
  [GC.FIELD_STOP_DROP]: R.prop(GC.FIELD_STOP_DROP, options),
  [GC.FIELD_EVENT_TYPE]: R.prop(GC.FIELD_EVENT_TYPE, options),
  [GC.FIELD_STOP_PICKUP]: R.prop(GC.FIELD_STOP_PICKUP, options),
  [GC.FIELD_TEL_EVENT_INDEX]: R.prop(GC.FIELD_TEL_EVENT_INDEX, options),
  [GC.FIELD_LOAD_EVENT_LATE_DATE]: R.prop(GC.FIELD_LOAD_EVENT_LATE_DATE, options),
  [GC.FIELD_LOAD_EVENT_EARLY_DATE]: R.prop(GC.FIELD_LOAD_EVENT_EARLY_DATE, options),
  // trailers
  [GC.FIELD_STOP_DROPPED_TRAILERS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILERS]: [],
  [GC.FIELD_STOP_DROPPED_TRAILER_GUIDS]: [],
  [GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS]: [],
});

export const generateDataForNewCloStopFromStopForm = (data, state) => {
  const { telGuid, loadGuid, location, eventType } = data;

  const telEventIndex = getNextTelEventIndex(telGuid, state);
  const drop = G.isEventTypeDrop(eventType);
  const pickup = G.isEventTypePickup(eventType);
  const locationData = omitLocationFields(location);
  const eventEarlyDate = R.propOr(null, GC.FIELD_LOAD_EVENT_EARLY_DATE, location);
  const eventLateDate = R.propOr(null, GC.FIELD_LOAD_EVENT_LATE_DATE, location);

  return {
    drop,
    pickup,
    telGuid,
    loadGuid,
    eventType,
    telEventIndex,
    eventLateDate,
    eventEarlyDate,
    cloGuid: loadGuid,
    location: locationData,
  };
};

const getCloItemsFromStoreByEventTypeAndTelGuid = (eventType, telGuid, state) => R.compose(
  R.reduce((acc, event) => R.concat(acc, event.items), []),
  R.filter((event) => R.and(
    R.propEq(eventType, GC.FIELD_EVENT_TYPE, event),
    R.propEq(GC.LOAD_TYPE_CLO, GC.FIELD_LOAD_TYPE, event),
  )),
  R.values,
  R.path(['currentRoute', 'tels', telGuid, 'events']),
)(state);

const getItemsFromStoreByEventTypeAndTelGuid = (eventType, telGuid, state) => R.compose(
  R.reduce((acc, event) => R.concat(acc, event.items), []),
  R.filter(R.propEq(eventType, GC.FIELD_EVENT_TYPE)),
  R.values,
  R.path(['currentRoute', 'tels', telGuid, 'events']),
)(state);

const filterItems = (items1 = [], items2 = []) => R.filter(
  (item) => R.isNil(R.find(R.eqProps(GC.FIELD_GUID, item), items2)),
  items1,
);

export const addTerminalDropsToCurrentRouteTels = (
  stopId,
  data,
  state,
) => {
  const dropTelsGuids = R.pathOr([], ['returnObject', 'dropTels'], data);
  const stopData = R.compose(
    R.assoc('eventType', GC.EVENT_TYPE_DROP),
    R.omit('returnObject'),
  )(data);
  const terminalDrops = R.compose(
    R.indexBy(R.prop(GC.FIELD_TEL_GUID)),
    R.filter(G.isNotNil),
    R.map((telGuid) => {
      const terminalDropExists = R.compose(
        R.find((telEvent) => G.isAllTrue(
          G.isStopDrop(telEvent),
          G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, telEvent)),
          R.equals(
            R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], data),
            R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], telEvent),
          ),
        )),
        R.values,
        R.path(['currentRoute', 'tels', telGuid, GC.FIELD_LOAD_STOPS]),
      )(state);

      if (G.isNotNil(terminalDropExists)) return null;

      const stopDataWithTelGuid = R.assoc('loadGuid', telGuid, stopData);
      const stopOptions = generateDataForNewTelStopFromStopForm(stopDataWithTelGuid, state);
      const cloPickupItems = getCloItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_PICKUP, telGuid, state);
      const cloDropItems = getItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_DROP, telGuid, state);
      const availableItems = filterItems(cloPickupItems, cloDropItems);

      let newTerminal = createNewTerminalOnRouteTel(R.assoc('stopId', stopId, stopOptions));

      if (G.isNotNilAndNotEmpty(availableItems)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            items: availableItems,
            itemIds: R.map((item) => item.itemInternalId, availableItems),
          },
        );
      }

      return newTerminal;
    }),
  )(dropTelsGuids);
  const newCurrentRouteTels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.map((tel) => {
      const telGuid = R.prop(GC.FIELD_GUID, tel);

      if (R.includes(telGuid, dropTelsGuids)) {
        const terminalDrop = R.pathOr(null, [telGuid], terminalDrops);

        if (R.isNil(terminalDrop)) return tel;

        return R.assocPath([GC.FIELD_LOAD_STOPS, R.prop(GC.FIELD_GUID, terminalDrop)], terminalDrop, tel);
      }

      return tel;
    }),
    R.values,
    R.path(['currentRoute', 'tels']),
  )(state);

  return P.$set('currentRoute.tels', newCurrentRouteTels, state);
};

const getAvailableContainersForTerminalPickup = (telGuid, stops) => {
  const droppedContainers = R.compose(
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.reduce((acc, event) => R.concat(acc, event.droppedContainers), []),
    R.values,
  )(stops);

  const pickedUpContainerIds = R.compose(
    R.reduce((acc, event) => R.concat(acc, event.pickedUpContainerIds), []),
    R.values,
  )(stops);

  return R.values(R.omit(pickedUpContainerIds, droppedContainers));
};

const getAvailableContainersForTerminalDrop = (telGuid, stops) => {
  const pickedUpContainers = R.compose(
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.reduce((acc, event) => R.concat(acc, event.pickedUpContainers), []),
    R.values,
  )(stops);

  const droppedContainerIds = R.compose(
    R.reduce((acc, event) => R.concat(acc, event.droppedContainerIds), []),
    R.values,
  )(stops);

  return R.values(R.omit(droppedContainerIds, pickedUpContainers));
};

export const addTerminalPickupsToCurrentRouteTels = (
  stopId,
  data,
  state,
) => {
  const pickupTelsGuids = R.pathOr([], ['returnObject', 'pickupTels'], data);

  const stopData = R.compose(
    R.assoc('eventType', GC.EVENT_TYPE_PICKUP),
    R.omit('returnObject'),
  )(data);

  const terminalPickups = R.compose(
    R.indexBy(R.prop(GC.FIELD_TEL_GUID)),
    R.filter(G.isNotNil),
    R.map((telGuid) => {
      const terminalPickupExists = R.compose(
        R.find((telEvent) => G.isAllTrue(
          G.isStopPickup(telEvent),
          G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, telEvent)),
          R.equals(
            R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], data),
            R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], telEvent),
          ),
        )),
        R.values,
        R.path(['currentRoute', 'tels', telGuid, GC.FIELD_LOAD_STOPS]),
      )(state);

      if (G.isNotNil(terminalPickupExists)) return null;

      const stopDataWithTelGuid = R.assoc('loadGuid', telGuid, stopData);
      const stopOptions = generateDataForNewTelStopFromStopForm(stopDataWithTelGuid, state);

      let newTerminal = createNewTerminalOnRouteTel(R.assoc('stopId', stopId, stopOptions));

      const cloDropItems = getCloItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_DROP, telGuid, state);
      const cloPickupItems = getItemsFromStoreByEventTypeAndTelGuid(GC.EVENT_TYPE_PICKUP, telGuid, state);
      const availableItems = filterItems(cloDropItems, cloPickupItems);

      if (G.isNotNilAndNotEmpty(availableItems)) {
        newTerminal = R.mergeRight(
          newTerminal,
          {
            items: availableItems,
            itemIds: R.map((item) => item.itemInternalId, availableItems),
          },
        );
      }

      const stops = R.path(['currentRoute', 'tels', telGuid, 'events'], state);
      const pickedUpContainers = getAvailableContainersForTerminalPickup(telGuid, stops);

      if (G.isNotNilAndNotEmpty(pickedUpContainers)) {
        newTerminal = {
          ...newTerminal,
          pickedUpContainers,
          [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers),
        };
      }

      return newTerminal;
    }),
  )(pickupTelsGuids);
  const newCurrentRouteTels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.map((tel) => {
      const telGuid = R.prop(GC.FIELD_GUID, tel);

      if (R.includes(telGuid, pickupTelsGuids)) {
        const terminalPickup = R.pathOr(null, [telGuid], terminalPickups);

        if (R.isNil(terminalPickup)) return tel;

        const newTel = R.assocPath([GC.FIELD_LOAD_STOPS, R.prop(GC.FIELD_GUID, terminalPickup)], terminalPickup, tel);

        const newTelEvents = R.compose(
          R.indexBy(R.prop(GC.FIELD_GUID)),
          R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
          R.map((event) => {
            if (R.equals(event.guid, terminalPickup.guid)) {
              return R.assoc(GC.FIELD_TEL_EVENT_INDEX, 1, event);
            }

            return R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(event.telEventIndex), event);
          }),
          R.values,
          R.prop(GC.FIELD_LOAD_STOPS),
        )(newTel);

        return R.assoc(GC.FIELD_LOAD_STOPS, newTelEvents, newTel);
      }

      return tel;
    }),
    R.values,
    R.path(['currentRoute', 'tels']),
  )(state);

  return P.$set('currentRoute.tels', newCurrentRouteTels, state);
};

export const updateCurrentRouteTelsTerminalStops = (
  eventToUpdate,
  newLocation,
  state,
) => {
  const { stopType, stopId } = eventToUpdate;

  if (R.equals(stopType, GC.STOP_TYPE_TERMINAL)) {
    const newCurrentRouteTels = R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      R.map((tel) => {
        const newTelEvents = R.compose(
          R.indexBy(R.prop(GC.FIELD_GUID)),
          R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
          R.map((event) => {
            if (R.equals(stopId, R.prop('stopId', event))) {
              return R.assoc('location', R.omit(['contacts', 'comments'], newLocation), event);
            }

            return event;
          }),
          R.values,
          R.prop(GC.FIELD_LOAD_STOPS),
        )(tel);

        return R.assoc(GC.FIELD_LOAD_STOPS, newTelEvents, tel);
      }),
      R.values,
      R.path(['currentRoute', 'tels']),
    )(state);

    return P.$set('currentRoute.tels', newCurrentRouteTels, state);
  }

  return state;
};

export const createTerminalStopWithExistedStopId = (options) => {
  const newStop = createNewStopOnRouteTel(options);

  return {
    ...newStop,
    stopId: options.stopId,
    stopType: GC.STOP_TYPE_TERMINAL,
  };
};

export const getFirstTerminalEventLocationByStopIdFromState = (stopId, state) => R.compose(
  R.prop(GC.FIELD_LOCATION),
  R.head,
  R.reduce((acc, item) => R.concat(acc, item), []),
  R.map((tel) => R.compose(
    R.filter(R.propEq(stopId, 'stopId')),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel)),
  R.values,
  R.path(['currentRoute', 'tels']),
)(state);

export const isEqualSourceAndDestinationLoad = (result) => (
  R.equals(
    R.path(['source', 'droppableId'], result),
    R.path(['destination', 'droppableId'], result),
  )
);

export const recreateTelEventsAfterDnD = R.compose(
  R.indexBy(R.prop(GC.FIELD_GUID)),
  R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
  G.mapIndexed((event, index) => R.assoc('telEventIndex', R.inc(index), event)),
);

export const getIndexesFromDnDResult = (result) => {
  const sourceIndex = R.path(['source', 'index'], result);
  const destinationIndex = R.path(['destination', 'index'], result);

  const destinationIndexChecked = G.ifElse(
    R.equals(destinationIndex, -1),
    0,
    destinationIndex,
  );

  return {
    sourceIndex,
    destinationIndexChecked,
  };
};

export const reorderTelEvents = (
  sourceIndex,
  destinationIndex,
  events,
) => {
  const eventsArray = sortTelEvents(events);

  const [removedEvent] = eventsArray.splice(sourceIndex, 1);

  eventsArray.splice(destinationIndex, 0, removedEvent);

  return recreateTelEventsAfterDnD(eventsArray);
};

export const reorderTelEventsInState = (state, result) => {
  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);

  const sourceTelGuid = R.path(['source', 'droppableId'], result);
  const telEventsState = R.path(['currentRoute', 'tels', sourceTelGuid, GC.FIELD_LOAD_STOPS], state);
  const newTelEvents = reorderTelEvents(sourceIndex, destinationIndexChecked, telEventsState);

  return P.$set(
    `currentRoute.tels.${sourceTelGuid}.events`,
    newTelEvents,
    state,
  );
};

export const reorderTelEventsInCurrentRoute = (currentRoute, result) => {
  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);

  const sourceTelGuid = R.path(['source', 'droppableId'], result);
  const telEventsState = R.path(['tels', sourceTelGuid, GC.FIELD_LOAD_STOPS], currentRoute);
  const newTelEvents = reorderTelEvents(sourceIndex, destinationIndexChecked, telEventsState);

  return P.$set(
    `tels.${sourceTelGuid}.events`,
    newTelEvents,
    currentRoute,
  );
};

const checkDestinationTelEventsItems = (telEvents, { items, eventType }) => {
  const isPickup = G.isEventTypePickup(eventType);

  const terminalsCount = R.length(R.filter(
    (stop) => R.and(
      (R.propEq(GC.STOP_TYPE_TERMINAL, GC.FIELD_STOP_TYPE), stop),
      G.ifElse(isPickup, G.isStopDrop(stop), G.isStopPickup(stop)),
    ),
    telEvents,
  ));

  if ((G.notEquals(terminalsCount, 1))) return telEvents;

  const terminals = R.compose(
    R.map((terminal) => {
      if (R.equals(isPickup, G.isStopPickup(terminal))) return terminal;

      const newItems = R.concat(terminal.items, items);

      return R.mergeRight(
        terminal,
        {
          items: newItems,
          itemIds: R.map((item) => item.itemInternalId, newItems),
        },
      );
    }),
    R.filter(R.propEq(GC.STOP_TYPE_TERMINAL, GC.FIELD_STOP_TYPE)),
  )(telEvents);

  if ((G.notEquals(R.length(terminals), 1))) return telEvents;

  return R.map(
    (event) => {
      const existInTerminals = R.find(R.eqProps(GC.FIELD_GUID, event), terminals);

      if (existInTerminals) return existInTerminals;

      return event;
    },
    telEvents,
  );
};

const checkSourceTelEventsItems = (events, movedStop) => R.map(
  (event) => {
    const { stopType } = event;

    if (R.not(G.isStopTypeTerminal(stopType))) return event;

    const { itemIds } = movedStop;

    const items = R.filter(
      ({ itemInternalId }) => G.notContain(itemInternalId, itemIds),
    )(event.items);

    return R.mergeRight(event, {
      items,
      itemIds: R.map((item) => item.itemInternalId, items),
    });
  },
)(events);

export const moveEventFromTelToTel = (state, result, movedStop) => {
  const sourceTelGuid = R.path(['source', 'droppableId'], result);
  const destinationTelGuid = R.path(['destination', 'droppableId'], result);

  const sourceTelEventsArray = sortTelEvents(R.path(
    ['currentRoute', 'tels', sourceTelGuid, GC.FIELD_LOAD_STOPS],
    state,
  ));

  const destinationTelEventsArray = sortTelEvents(R.path(
    ['currentRoute', 'tels', destinationTelGuid, GC.FIELD_LOAD_STOPS],
    state,
  ));

  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);
  const [removedEvent] = sourceTelEventsArray.splice(sourceIndex, 1);

  destinationTelEventsArray.splice(destinationIndexChecked, 0, removedEvent);
  const sourceEvents = checkSourceTelEventsItems(sourceTelEventsArray, movedStop);
  const destinationEvents = checkDestinationTelEventsItems(destinationTelEventsArray, movedStop);

  return P.$all(
    P.$set(
      `currentRoute.tels.${sourceTelGuid}.events`,
      recreateTelEventsAfterDnD(sourceEvents),
    ),
    P.$set(
      `currentRoute.tels.${destinationTelGuid}.events`,
      recreateTelEventsAfterDnD(R.map(
        R.assoc('telGuid', destinationTelGuid),
        destinationEvents,
      )),
    ),
    state,
  );
};

const mapCloEvents = (eventsCount, events, telGuid) => G.mapIndexed(
  (event, index) => R.assoc(
    GC.FIELD_TEL_GUID,
    telGuid,
    R.assoc(
      GC.FIELD_TEL_EVENT_INDEX,
      R.add(
        R.inc(index),
        eventsCount,
      ),
      event,
    ),
  ), events);

export const createNewEventsForTel = (cloEvents, tel) => {
  const telEvents = R.values(R.prop(GC.FIELD_LOAD_STOPS, tel));
  const telEventsCount = R.length(telEvents);

  const mappedCloEvents = mapCloEvents(
    telEventsCount,
    cloEvents,
    R.prop(GC.FIELD_GUID, tel),
  );

  return R.indexBy(R.prop(GC.FIELD_GUID), R.concat(telEvents, mappedCloEvents));
};

const filterAndMergeTelEventItems = (telEvent, itemsToFilter) => R.mergeRight(telEvent, {
  itemIds: R.difference(telEvent.itemIds, itemsToFilter),
  items: R.filter(
    (item) => R.all(
      (id) => R.not(R.propEq(id, GC.FIELD_ITEM_INTERNAL_ID, item)),
      itemsToFilter,
    ),
    telEvent.items,
  ),
});

export const getFilteredTels = (
  tels,
  stopTel,
  stop,
  itemsToFilter,
) => {
  const isCloStop = G.isLoadTypeClo(stop);
  const isCloPickup = R.and(isCloStop, G.isStopPickup(stop));
  const isTerminal = G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop));
  const templateId = R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_ID], stop);

  if (isCloStop) {
    return R.map(
      (routeTel) => R.assoc(
        GC.FIELD_LOAD_STOPS,
        R.map(
          (telEvent) => {
            if (R.or(isCloPickup, R.eqProps(GC.FIELD_GUID, telEvent, stop))) {
              return filterAndMergeTelEventItems(telEvent, itemsToFilter);
            }

            return telEvent;
          },
          routeTel[GC.FIELD_LOAD_STOPS],
        ),
        routeTel,
      ),
      tels,
    );
  }

  return R.map(
    (routeTel) => {
      const hasSameTerminal = R.compose(
        R.find(R.pathEq(templateId, [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_ID])),
        R.values,
        R.prop(GC.FIELD_LOAD_STOPS),
      )(routeTel);

      if (R.gte(routeTel.order, stopTel.order)) {
        return R.assoc(
          GC.FIELD_LOAD_STOPS,
          R.map(
            (telEvent) => {
              if (R.or(
                G.isLoadTypeClo(telEvent),
                R.and(
                  R.eqProps(GC.FIELD_GUID, routeTel, stopTel),
                  R.lt(telEvent.telEventIndex, stop.telEventIndex),
                ),
              )) return telEvent;

              if (R.or(
                R.and(isTerminal, hasSameTerminal),
                R.propEq(GC.STOP_TYPE_INDEPENDENT, GC.FIELD_STOP_TYPE, stop),
              )) {
                return filterAndMergeTelEventItems(telEvent, itemsToFilter);
              }

              return telEvent;
            },
            routeTel[GC.FIELD_LOAD_STOPS],
          ),
          routeTel,
        );
      }

      return routeTel;
    },
    tels,
  );
};

export const removeEventItemsFromEvent = (event, removeEvent) => R.mergeRight(
  event,
  {
    itemIds: R.without(removeEvent.itemIds, event.itemIds),
    items: R.filter(
      (item) => R.isNil(R.find(R.eqProps(GC.FIELD_GUID, item), removeEvent.items)),
      event.items,
    ),
  },
);

export const getRemoveEventByGuid = (eventGuid, tels) => {
  let removeEvent;

  R.forEach(
    (tel) => R.forEach(
      (telEvent) => {
        if (R.propEq(eventGuid, GC.FIELD_GUID, telEvent)) removeEvent = telEvent;
      },
      R.values(R.prop(GC.FIELD_LOAD_STOPS, tel)),
    ),
    R.values(tels),
  );

  return removeEvent;
};

export const removeCloEventFromTels = (removeEvent, tels) => R.mapObjIndexed((tel) => (
  R.assoc(
    GC.FIELD_LOAD_STOPS,
    R.indexBy(
      R.prop(GC.FIELD_GUID),
      sortTelEvents(
        R.filter(
          (event) => R.not(R.eqProps(GC.FIELD_GUID, removeEvent, event)),
          R.prop(GC.FIELD_LOAD_STOPS, tel),
        ),
      ).map(
        (event, index) => {
          let eventToUse;

          if (G.isLoadTypeClo(event)) {
            eventToUse = event;
          } else {
            eventToUse = removeEventItemsFromEvent(event, removeEvent);
          }

          return R.assoc(
            GC.FIELD_TEL_EVENT_INDEX,
            R.inc(index),
            eventToUse,
          );
        },
      ),
    ),
    tel,
  )
), tels);

export const refreshRouteContainers = (tels = {}) => {
  const stops = R.values(mergeArrayItemsProp(R.values(tels), GC.FIELD_LOAD_STOPS));
  const picked = concatArrayItemsProp(stops, GC.FIELD_STOP_PICKED_UP_CONTAINERS);
  const dropped = concatArrayItemsProp(stops, GC.FIELD_STOP_DROPPED_CONTAINERS);

  return R.compose(
    R.map((entity) => R.mergeRight(entity, {
      isNew: false,
      selected: true,
    })),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.concat(dropped),
  )(picked);
};

const concatAndFilterContainerDuplicates = (arr1, arr2) => R.compose(
  R.values,
  R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
  R.concat(arr2),
)(arr1);

export const addCloEventToInterTerminal = (state, data) => {
  const { event, telGuid, matchEvent, templateId } = data;

  const tels = P.$get('currentRoute.tels', state);
  const tel = P.$get(`currentRoute.tels.${telGuid}`, state);
  const cloName = R.prop(GC.GRC.CLO_PRIMARY_REFERENCE_VALUE, event);

  const newContainers = R.compose(
    R.map(R.assoc(C.CLO_NAME, cloName)),
    R.propOr([], GC.FIELD_STOP_DROPPED_CONTAINERS),
  )(matchEvent);

  const telEvents = R.compose(
    R.map((telEvent) => {
      const isMatchedTerminalPickup = R.and(
        G.isStopPickup(telEvent),
        R.equals(
          R.prop(GC.GRC.LAST_TERMINAL_DROP_TEMPLATE_ID, event),
          R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], telEvent),
        ),
      );

      const isMatchedTerminalDrop = R.and(
        G.isStopDrop(telEvent),
        R.pathEq(templateId, [GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], telEvent),
      );

      if (R.or(
        isMatchedTerminalDrop,
        isMatchedTerminalPickup,
      )) {
        let droppedContainers = R.propOr([], GC.FIELD_STOP_DROPPED_CONTAINERS, telEvent);
        let pickedUpContainers = R.propOr([], GC.FIELD_STOP_PICKED_UP_CONTAINERS, telEvent);

        if (isMatchedTerminalDrop) {
          droppedContainers = concatAndFilterContainerDuplicates(droppedContainers, newContainers);
        } else {
          pickedUpContainers = concatAndFilterContainerDuplicates(pickedUpContainers, newContainers);
        }

        return R.mergeRight(telEvent, {
          droppedContainers,
          pickedUpContainers,
          [GC.FIELD_STOP_ITEM_IDS]: R.uniq(R.concat(telEvent.itemIds, matchEvent.itemIds)),
          [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), droppedContainers),
          [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers),
          [GC.FIELD_STOP_ITEMS]: R.uniqBy(
            R.prop(GC.FIELD_ITEM_INTERNAL_ID),
            R.concat(telEvent.items, matchEvent.items),
          ),
          [GC.FIELD_RELATED_DROP_GUIDS]: R.uniq(R.append(
            G.getGuidFromObject(event),
            R.prop(GC.FIELD_RELATED_DROP_GUIDS, telEvent),
          )),
        });
      }

      return telEvent;
    }),
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const newTels = R.assoc(
    telGuid,
    {
      ...tel,
      [GC.FIELD_LOAD_STOPS]: telEvents,
      cloPrimaryReferenceValues: R.assoc(
        R.prop(GC.FIELD_CLO_GUID, event),
        cloName,
        R.or(tel.cloPrimaryReferenceValues, {}),
      ),
    },
    tels,
  );

  return P.$all(
    P.$set('currentRoute.tels', newTels),
    P.$set('currentRouteContainers', refreshRouteContainers(newTels)),
    P.$set('currentRouteItems', addTelItems(state.currentRouteItems, R.path([GC.FIELD_STOP_ITEMS], matchEvent))),
    state,
  );
};

const getTelEventsWithAddedEvent = (state, matchEvent, tel, templateId) => {
  const stops = R.compose(
    R.assoc(R.prop(GC.FIELD_ITEM_INTERNAL_ID, matchEvent), matchEvent),
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  let telEvents;

  if (G.isStopDrop(matchEvent)) {
    telEvents = R.compose(
      R.assoc(R.prop(GC.FIELD_GUID, matchEvent), matchEvent),
      R.map((telEvent) => {
        const isMatchTerminal = G.isAllTrue(
          G.isNotNil(templateId),
          G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, telEvent)),
          R.equals(templateId, R.path([GC.FIELD_LOCATION, GC.FIELD_TEMPLATE_ID], telEvent)),
        );

        if (R.not(isMatchTerminal)) return telEvent;

        if (G.isStopDrop(telEvent)) {
          const availableContainers = getAvailableContainersForTerminalDrop(G.getGuidFromObject(tel), stops);

          const droppedContainers = R.compose(
            R.concat(availableContainers),
            R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS),
          )(telEvent);

          return R.mergeRight(telEvent, {
            droppedContainers,
            itemIds: R.without(matchEvent.itemIds, telEvent.itemIds),
            [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), droppedContainers),
            items: R.filter(
              (item) => R.isNil(R.find(
                R.equals(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item)),
                R.prop(GC.FIELD_STOP_ITEM_IDS, matchEvent),
              )),
              telEvent.items,
            ),
          });
        }

        const availableContainers = getAvailableContainersForTerminalPickup(G.getGuidFromObject(tel), stops);

        const pickedUpContainers = R.compose(
          R.concat(availableContainers),
          R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS),
        )(telEvent);

        return R.mergeRight(telEvent, {
          pickedUpContainers,
          itemIds: R.uniq(R.concat(telEvent.itemIds, matchEvent.itemIds)),
          items: R.uniqBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID), R.concat(telEvent.items, matchEvent.items)),
          [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers),
        });
      }),
    )(stops);
  } else {
    const singleTerminalDrop = R.compose(
      R.equals(1),
      R.length,
      R.filter((telEvent) => R.and(
        G.isStopDrop(telEvent),
        G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, telEvent)),
      )),
      R.values,
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tel);

    const isItemsDropped = isEventItemsDropped(matchEvent, tel);

    telEvents = R.compose(
      R.assoc(R.prop(GC.FIELD_GUID, matchEvent), matchEvent),
      R.map((telEvent) => {
        const isDrop = G.isStopDrop(telEvent);
        const isTerminal = G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, telEvent));

        let telEventToUse = telEvent;

        if (isTerminal) {
          if (isDrop) {
            const availableContainers = getAvailableContainersForTerminalDrop(G.getGuidFromObject(tel), stops);

            const droppedContainers = R.compose(
              R.concat(availableContainers),
              R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS),
            )(telEvent);

            telEventToUse = {
              ...telEventToUse,
              droppedContainers,
              [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), droppedContainers),
            };
          }

          const availableContainers = getAvailableContainersForTerminalPickup(G.getGuidFromObject(tel), stops);

          const pickedUpContainers = R.compose(
            R.concat(availableContainers),
            R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS),
          )(telEvent);

          telEventToUse = {
            ...telEventToUse,
            pickedUpContainers,
            [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), pickedUpContainers),
          };
        }

        const condition = G.isAllTrue(
          isDrop,
          singleTerminalDrop,
          R.not(isItemsDropped),
          R.not(G.isLoadTypeClo(telEvent)),
        );

        if (condition) {
          return R.mergeRight(telEventToUse, {
            itemIds: R.uniq(R.concat(telEvent.itemIds, matchEvent.itemIds)),
            items: R.uniqBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID), R.concat(telEvent.items, matchEvent.items)),
          });
        }

        if (G.isStopPickup(telEvent)) {
          return R.mergeRight(telEventToUse, {
            itemIds: R.without(matchEvent.itemIds, telEvent.itemIds),
            items: R.filter(
              (item) => R.isNil(R.find(
                R.equals(R.prop(GC.FIELD_ITEM_INTERNAL_ID, item)),
                R.prop(GC.FIELD_STOP_ITEM_IDS, matchEvent),
              )),
              telEvent.items,
            ),
          });
        }

        return telEvent;
      }),
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tel);
  }

  return telEvents;
};

const mapStopContainers = (stop) => {
  const commonData = R.pick([C.CLO_NAME, GC.FIELD_CLO_GUID, GC.FIELD_LOAD_TYPE], stop);

  return {
    ...stop,
    [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.compose(
      R.map((container) => ({ ...commonData, ...container })),
      R.propOr([], GC.FIELD_STOP_DROPPED_CONTAINERS),
    )(stop),
    [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.compose(
      R.map((container) => ({ ...commonData, ...container })),
      R.propOr([], GC.FIELD_STOP_PICKED_UP_CONTAINERS),
    )(stop),
  };
};

export const mapRouteContainers = (tels = []) => {
  const cloPrimaryReferenceValues = R.reduce(
    (acc, tel) => R.mergeRight(acc, tel.cloPrimaryReferenceValues),
    {},
    tels,
  );

  const cloStops = R.filter(
    G.isLoadTypeClo,
    concatArrayItemsProp(tels, GC.FIELD_LOAD_STOPS),
  );

  return R.compose(
    R.map((container) => {
      const { loadGuid } = container;

      return R.assoc(C.CLO_NAME, R.prop(loadGuid, cloPrimaryReferenceValues), container);
    }),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.concat(concatArrayItemsProp(cloStops, GC.FIELD_STOP_DROPPED_CONTAINERS)),
  )(concatArrayItemsProp(cloStops, GC.FIELD_STOP_PICKED_UP_CONTAINERS));
};

export const selectCloEventForTel = (state, data) => {
  const { event, telGuid, templateId, matchEvent } = data;

  const { CLO_NAME, cloGuid } = matchEvent;

  const cloEventWithContainers = mapStopContainers(matchEvent);
  const tel = R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid], state);
  const telEvents = getTelEventsWithAddedEvent(state, cloEventWithContainers, tel, templateId);

  const newTel = {
    ...tel,
    [GC.FIELD_LOAD_STOPS]: sortTelEventsByDate(telEvents),
    cloPrimaryReferenceValues: R.assoc(
      cloGuid,
      CLO_NAME,
      R.propOr({}, 'cloPrimaryReferenceValues', tel),
    ),
  };

  const newTels = R.compose(
    R.assoc(telGuid, newTel),
    R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
  )(state);

  return P.$all(
    P.$set(`currentRoute.tels.${telGuid}`, newTel),
    P.$set('currentRouteContainers', refreshRouteContainers(newTels)),
    P.$set('cloEventList', selectItem(state.cloEventList, R.prop(GC.FIELD_GUID, event), true)),
    P.$set('currentRouteItems', addTelItems(state.currentRouteItems, R.path([GC.FIELD_STOP_ITEMS], matchEvent))),
    state,
  );
};

export const addUnassignedEventToPlanner = (state, data) => {
  const telGuid = R.path(['selectedValue', 'value'], data);
  const event = R.path(['returnObject', 'event'], data);
  const tel = R.path(['currentRoute', GC.SYSTEM_LIST_TELS, telGuid], state);
  const telEvents = getTelEventsWithAddedEvent(state, event, tel);

  const unassignedCloEvents = R.compose(
    R.filter((unassignedEvent) => R.not(R.eqProps(GC.FIELD_GUID, event, unassignedEvent))),
    R.prop('unassignedCloEvents'),
  )(state);

  return P.$all(
    P.$set('unassignedCloEvents', unassignedCloEvents),
    P.$set('currentRouteItems', addTelItems(state.currentRouteItems, R.path([GC.FIELD_STOP_ITEMS], event))),
    P.$set(`currentRoute.tels.${telGuid}`, R.assoc(GC.FIELD_LOAD_STOPS, sortTelEventsByDate(telEvents), tel)),
    state,
  );
};

const filterTelEventContainers = (event, containerIds) => R.mergeRight(event, {
  [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.without(
    containerIds,
    R.prop(GC.FIELD_STOP_DROPPED_CONTAINER_IDS, event),
  ),
  [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.without(
    containerIds,
    R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event),
  ),
  [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.compose(
    R.values,
    R.omit(containerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS),
  )(event),
  [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.compose(
    R.values,
    R.omit(containerIds),
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINERS),
  )(event),
});

const filterEventItemsFromContainers = (event, containerIds) => R.assoc(
  GC.FIELD_LOAD_ITEMS,
  R.map(
    (item) => {
      const { containerInternalId } = item;
      if (R.includes(containerInternalId, containerIds)) {
        return R.mergeRight(item, {
          [GC.FIELD_CONTAINER_GUID]: null,
          [GC.FIELD_CONTAINER_INTERNAL_ID]: null,
        });
      }
      return item;
    },
    R.prop(GC.FIELD_LOAD_ITEMS, event),
  ),
  event,
);

export const getTelsFilteredFromContainers = (
  tels,
  stop,
  isPickupContainer,
  containersToFilter,
) => {
  const { stopType } = stop;

  const isCloStop = G.isLoadTypeClo(stop);
  const isPickup = G.isStopPickup(stop);
  const isTerminal = G.isStopTypeTerminal(stopType);

  return R.map(
    (routeTel) => R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.map(
        (telEvent) => {
          const cond1 = R.and(isCloStop, isPickupContainer);
          const cond2 = R.eqProps(GC.FIELD_GUID, telEvent, stop);
          const cond3 = R.and(R.not(isPickup), R.not(G.isLoadTypeClo(telEvent)));

          if (R.and(isTerminal, R.not(cond2))) return telEvent;

          if (G.isAnyTrue(cond1, cond2, cond3)) {
            const eventToUse = G.ifElse(
              cond1,
              filterEventItemsFromContainers(telEvent, containersToFilter),
              telEvent,
            );

            return filterTelEventContainers(eventToUse, containersToFilter);
          }

          return telEvent;
        },
        routeTel[GC.FIELD_LOAD_STOPS],
      ),
      routeTel,
    ),
    tels,
  );
};

const concatObjectItemsIndexedProp = (obj = {}, prop) => R.reduce(
  R.concat,
  [],
  R.map((entity = {}) => R.values(entity[prop]), obj),
);

export const refreshRouteItems = (tels) => R.indexBy(
  R.prop(GC.FIELD_ITEM_INTERNAL_ID),
  R.compose(
    R.reverse,
    R.sortBy(R.prop(C.CLO_NAME)),
  )(concatArrayItemsProp(
    concatObjectItemsIndexedProp(R.values(tels), GC.FIELD_LOAD_STOPS),
    GC.FIELD_LOAD_ITEMS,
  )),
);

const getItemsToFilter = (tel) => R.uniq(R.reduce(
  (arr = [], event) => R.concat(arr, event.itemIds),
  [],
  R.values(R.prop(GC.FIELD_LOAD_STOPS, tel)),
));

const getTelsAndItemsToRemoveAfterRemoveTel = (state, tel) => {
  const itemsToFilter = getItemsToFilter(tel);
  const tels = R.dissoc(G.getGuidFromObject(tel), R.path(['currentRoute', GC.SYSTEM_LIST_TELS], state));
  const itemsToRemove = getItemsToRemove(itemsToFilter, tels);

  return { tels, itemsToRemove };
};

export const handleDeleteTel = (state, guid, unassign = false) => {
  const tel = R.path(['currentRoute', 'tels', guid], state);

  const cloEventList = R.compose(
    R.map((cloEvent) => {
      const existsInTel = R.compose(
        R.find(R.eqProps(GC.FIELD_GUID, cloEvent)),
        R.values,
        R.prop(GC.FIELD_LOAD_STOPS),
      )(tel);

      if (existsInTel) return R.assoc('selected', false, cloEvent);

      return cloEvent;
    }),
    R.prop('cloEventList'),
  )(state);

  const { tels } = getTelsAndItemsToRemoveAfterRemoveTel(state, tel);

  const newState = P.$all(
    P.$set('cloEventList', cloEventList),
    P.$set('currentRouteItems', refreshRouteItems(tels)),
    P.$set('currentRoute.tels', R.omit(R.of(Array, tel.guid), tels)),
    state,
  );

  if (R.or(G.isTrue(R.prop('isNew', tel)), unassign)) return newState;

  const deletedTelGuids = R.compose(
    R.append(R.prop(GC.FIELD_GUID, tel)),
    R.pathOr([], ['currentRoute', 'deletedTelGuids']),
  )(newState);

  return P.$set('currentRoute.deletedTelGuids', deletedTelGuids, newState);
};

const unselectTelInList = (state, guid, listName) => R.compose(
  R.map((telItem) => {
    if (R.propEq(guid, GC.FIELD_GUID, telItem)) return R.assoc('selected', false, telItem);

    return telItem;
  }),
  R.prop(listName),
)(state);

export const removeTelFromPlanner = (state, guid) => {
  const { currentRoute, selectedTels, cloEventList, deletedEventGuids } = state;

  const isInDeletedList = R.compose(
    G.isNotNil,
    R.find(R.equals(guid)),
    R.pathOr([], [C.DELETED_TEL_GUIDS]),
  )(currentRoute);

  if (isInDeletedList) {
    let tels = R.prop(GC.SYSTEM_LIST_TELS, currentRoute);

    const telEvents = R.compose(
      R.pathOr([], ['details', GC.FIELD_LOAD_STOPS]),
      R.find(R.propEq(guid, GC.FIELD_GUID)),
      R.prop('telList'),
    )(state);

    const telEventGuids = R.map(G.getGuidFromObject, telEvents);

    const newCloEventList = R.map(
      (event) => {
        const { guid } = event;

        if (R.includes(guid, telEventGuids)) {
          return R.assoc('selected', false, event);
        }

        return event;
      },
      cloEventList,
    );

    R.forEach(
      (removeEvent) => {
        tels = removeCloEventFromTels(removeEvent, tels);
      },
      telEvents,
    );

    return P.$all(
      P.$set('currentRoute.tels', tels),
      P.$set('cloEventList', newCloEventList),
      P.$set('currentRouteItems', refreshRouteItems(tels)),
      P.$set('telList', unselectTelInList(state, guid, 'telList')),
      P.$set('selectedTels', R.omit(R.of(Array, guid), selectedTels)),
      P.$set('deletedEventGuids', R.without(telEventGuids, deletedEventGuids)),
      P.$set('inboundTelList', unselectTelInList(state, guid, 'inboundTelList')),
      P.$set('outboundTelList', unselectTelInList(state, guid, 'outboundTelList')),
      P.$set('currentRoute.deletedTelGuids', R.without(R.of(Array, guid), R.prop(C.DELETED_TEL_GUIDS, currentRoute))),
      state,
    );
  }

  const newState = handleDeleteTel(state, guid, true);

  return P.$all(
    P.$set('selectedTels', R.omit(R.of(Array, guid), selectedTels)),
    P.$set('telList', unselectTelInList(newState, guid, 'telList')),
    P.$set('inboundTelList', unselectTelInList(newState, guid, 'inboundTelList')),
    P.$set('outboundTelList', unselectTelInList(newState, guid, 'outboundTelList')),
    newState,
  );
};

export const getCurrentRouteWithExchangeTerminal = (tel, currentRoute, terminal) => {
  const order = R.length(R.values(R.path(['tels'], currentRoute)));
  const telGuid = G.generateGuid();

  const telInfo = {
    order,
    [GC.FIELD_GUID]: telGuid,
    [GC.FIELD_BRANCH_GUID]: R.prop(GC.FIELD_BRANCH_GUID, tel),
    [GC.FIELD_PRIMARY_REFERENCE]: { value: `Tel-${R.inc(order)}` },
  };

  const telDrops = R.compose(
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    R.map((stop) => R.mergeRight(stop, {
      [GC.FIELD_TEL_GUID]: telGuid,
      [GC.FIELD_LOAD_GUID]: telGuid,
    })),
    R.filter(G.isStopDrop),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const dates = R.pick(
    [GC.FIELD_LOAD_EVENT_EARLY_DATE, GC.FIELD_LOAD_EVENT_LATE_DATE],
    R.head(telDrops),
  );

  const exchangeItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_LOAD_ITEMS)),
  )(telDrops);

  const itemsObject = {
    items: exchangeItems,
    itemIds: R.map((item) => item.itemInternalId, exchangeItems),
  };

  const newTerminalDrop = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    [GC.FIELD_LOCATION]: terminal,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_DROP,
    [GC.FIELD_TEL_GUID]: R.prop(GC.FIELD_GUID, tel),
    [GC.FIELD_LOAD_GUID]: R.prop(GC.FIELD_GUID, tel),
  }), itemsObject);

  const newTerminalPickup = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    [GC.FIELD_LOCATION]: terminal,
    [GC.FIELD_TEL_GUID]: telGuid,
    [GC.FIELD_LOAD_GUID]: telGuid,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_PICKUP,
  }), itemsObject);

  const newTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.prepend(newTerminalPickup),
  )(telDrops);

  const newTel = R.assoc(GC.FIELD_LOAD_STOPS, newTelStops, createNewRouteTel(telInfo));

  const splittedTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.append(newTerminalDrop),
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    R.filter((stop) => R.not(G.isStopDrop(stop))),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const splittedTel = R.assoc(GC.FIELD_LOAD_STOPS, splittedTelStops, tel);

  const newRoute = R.compose(
    R.assocPath([GC.SYSTEM_LIST_TELS, R.prop(GC.FIELD_GUID, tel)], splittedTel),
    R.assocPath([GC.SYSTEM_LIST_TELS, telGuid], newTel),
  )(currentRoute);

  return newRoute;
};

export const getCurrentRouteWithEventsOnExchangeTerminal = ({
  tel,
  events,
  currentRoute,
  terminalWithoutDates,
}) => {
  const order = R.length(R.values(R.path(['tels'], currentRoute)));
  const telGuid = G.generateGuid();

  const telInfo = {
    order: R.dec(order),
    [GC.FIELD_GUID]: telGuid,
    [GC.FIELD_PRIMARY_REFERENCE]: { value: `Tel-${order}` },
    [GC.FIELD_BRANCH_GUID]: R.prop(GC.FIELD_BRANCH_GUID, tel),
  };

  const exchangeItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_LOAD_ITEMS)),
  )(events);

  const exchangeItemIds = R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), exchangeItems);

  const itemsObject = {
    [GC.FIELD_STOP_ITEMS]: exchangeItems,
    [GC.FIELD_STOP_ITEM_IDS]: R.map((item) => item.itemInternalId, exchangeItems),
  };

  const telDrops = R.compose(
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    R.map((stop) => R.mergeRight(stop, {
      [GC.FIELD_TEL_GUID]: telGuid,
      [GC.FIELD_LOAD_GUID]: telGuid,
    })),
    R.filter(G.isStopDrop),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const dates = R.pick(
    [GC.FIELD_LOAD_EVENT_EARLY_DATE, GC.FIELD_LOAD_EVENT_LATE_DATE],
    R.head(telDrops),
  );

  const newTerminalDrop = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    [GC.FIELD_LOCATION]: terminalWithoutDates,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_DROP,
    [GC.FIELD_TEL_GUID]: R.prop(GC.FIELD_GUID, tel),
    [GC.FIELD_LOAD_GUID]: R.prop(GC.FIELD_GUID, tel),
  }), itemsObject);

  const newTerminalPickup = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    [GC.FIELD_TEL_GUID]: telGuid,
    [GC.FIELD_LOAD_GUID]: telGuid,
    [GC.FIELD_LOCATION]: terminalWithoutDates,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_PICKUP,
  }), itemsObject);

  const splittedTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.prepend(newTerminalPickup),
    R.filter((stop) => G.isNotEmpty(R.prop(GC.FIELD_STOP_ITEM_IDS, stop))),
    R.map((stop) => {
      if (G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop))) {
        const stopItems = R.compose(
          R.omit(exchangeItemIds),
          R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
          R.prop(GC.FIELD_STOP_ITEMS),
        )(stop);

        return R.mergeRight(stop, {
          [GC.FIELD_STOP_ITEMS]: R.values(stopItems),
          [GC.FIELD_STOP_ITEM_IDS]: R.keys(stopItems),
        });
      }

      return stop;
    }),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const newTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.append(newTerminalDrop),
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
    R.filter((stop) => G.isNotEmpty(R.prop(GC.FIELD_STOP_ITEM_IDS, stop))),
    R.map((stop) => {
      const stopItems = R.compose(
        R.pick(exchangeItemIds),
        R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
        R.prop(GC.FIELD_STOP_ITEMS),
      )(stop);

      return R.mergeRight(stop, {
        [GC.FIELD_STOP_ITEMS]: R.values(stopItems),
        [GC.FIELD_STOP_ITEM_IDS]: R.keys(stopItems),
      });
    }),
    R.filter((stop) => R.and(
      G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop)),
      R.any(
        R.flip(R.includes(exchangeItemIds)),
        R.prop(GC.FIELD_STOP_ITEM_IDS, stop),
      ),
    )),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const newTel = R.assoc(GC.FIELD_LOAD_STOPS, newTelStops, createNewRouteTel(telInfo));

  const splittedTel = R.mergeRight(tel, {
    order,
    [GC.FIELD_LOAD_STOPS]: splittedTelStops,
  });

  const newRoute = R.compose(
    R.assocPath([GC.SYSTEM_LIST_TELS, R.prop(GC.FIELD_GUID, tel)], splittedTel),
    R.assocPath([GC.SYSTEM_LIST_TELS, telGuid], newTel),
  )(currentRoute);

  return newRoute;
};

export const getCurrentRouteWithSplitEvents = ({
  tel,
  events,
  currentRoute,
  terminalWithoutDates,
}) => {
  const order = R.length(R.values(R.path(['tels'], currentRoute)));
  const telGuid = G.generateGuid();

  const { stopId } = terminalWithoutDates;

  const splittedTelGuid = G.getGuidFromObject(tel);

  const telInfo = {
    order,
    [GC.FIELD_GUID]: telGuid,
    [GC.FIELD_BRANCH_GUID]: R.prop(GC.FIELD_BRANCH_GUID, tel),
    [GC.FIELD_PRIMARY_REFERENCE]: { value: `Tel-${R.inc(order)}` },
  };

  const exchangeItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_LOAD_ITEMS)),
  )(events);

  const itemsObject = {
    [GC.FIELD_STOP_ITEMS]: exchangeItems,
    [GC.FIELD_STOP_ITEM_IDS]: R.map((item) => item.itemInternalId, exchangeItems),
  };

  const telEvents = R.compose(
    R.filter((stop) => R.isNil(R.find(R.eqProps(GC.FIELD_GUID, stop), events))),
    R.values,
    R.prop(GC.FIELD_LOAD_STOPS),
  )(tel);

  const dates = R.compose(
    R.pick([GC.FIELD_LOAD_EVENT_EARLY_DATE, GC.FIELD_LOAD_EVENT_LATE_DATE]),
    R.head,
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
  )(events);

  const newTerminalDrop = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    stopId,
    [GC.FIELD_TEL_GUID]: splittedTelGuid,
    [GC.FIELD_LOAD_GUID]: splittedTelGuid,
    [GC.FIELD_LOCATION]: terminalWithoutDates,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_DROP,
  }), itemsObject);

  const newTerminalPickup = R.mergeRight(createNewTerminalOnRouteTel({
    ...dates,
    stopId,
    [GC.FIELD_TEL_GUID]: telGuid,
    [GC.FIELD_LOAD_GUID]: telGuid,
    [GC.FIELD_LOCATION]: terminalWithoutDates,
    [GC.FIELD_EVENT_TYPE]: GC.EVENT_TYPE_PICKUP,
  }), itemsObject);

  const newTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.prepend(newTerminalPickup),
    R.map((event) => R.mergeRight(event, {
      [GC.FIELD_TEL_GUID]: telGuid,
      [GC.FIELD_LOAD_GUID]: telGuid,
    })),
  )(events);

  const newTel = R.assoc(GC.FIELD_LOAD_STOPS, newTelStops, createNewRouteTel(telInfo));

  const splittedTelStops = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((stop, i) => R.assoc(GC.FIELD_TEL_EVENT_INDEX, R.inc(i), stop)),
    R.append(newTerminalDrop),
    R.sortBy(R.prop(GC.FIELD_TEL_EVENT_INDEX)),
  )(telEvents);

  const splittedTel = R.assoc(GC.FIELD_LOAD_STOPS, splittedTelStops, tel);

  const newRoute = R.compose(
    R.assocPath([GC.SYSTEM_LIST_TELS, R.prop(GC.FIELD_GUID, tel)], splittedTel),
    R.assocPath([GC.SYSTEM_LIST_TELS, telGuid], newTel),
  )(currentRoute);

  return newRoute;
};

export const getNewTelEventsFromRouteTemplate = ({ tel, telGuid, locations, firstEventWithInitDates }) => R.map(
  (event) => {
    const { type, eventLateDate, eventEarlyDate, locationTemplateGuid } = event;

    let dates = {};

    if (G.isNotNilAndNotEmpty(firstEventWithInitDates)) {
      const firstEventInitLateDate = G.getPropFromObject('initLateDate', firstEventWithInitDates);
      const firstEventInitEarlyDate = G.getPropFromObject('initEarlyDate', firstEventWithInitDates);

      dates = {
        [GC.FIELD_LOAD_EVENT_LATE_DATE]: firstEventInitLateDate,
        [GC.FIELD_LOAD_EVENT_EARLY_DATE]: firstEventInitEarlyDate,
      };

      if (G.notEquals(event, R.omit(['initLateDate', 'initEarlyDate'], firstEventWithInitDates))) {
        const firstEventEarlyDate = G.getPropFromObject(GC.FIELD_LOAD_EVENT_EARLY_DATE, firstEventWithInitDates);

        const lateDateRange = G.getDateRange(eventEarlyDate, eventLateDate, 'm');
        const earlyDateRange = G.getDateRange(firstEventEarlyDate, eventEarlyDate, 'm');
        const newEventEarlyDate = G.addDateTimeWithFormat(firstEventInitEarlyDate, earlyDateRange, 'minutes');
        const newEventLateDate = G.addDateTimeWithFormat(newEventEarlyDate, lateDateRange, 'minutes');

        dates = {
          [GC.FIELD_LOAD_EVENT_LATE_DATE]: newEventLateDate,
          [GC.FIELD_LOAD_EVENT_EARLY_DATE]: newEventEarlyDate,
        };
      }
    }

    const options = R.mergeAll([
      event,
      dates,
      {
        [GC.FIELD_EVENT_TYPE]: type,
        [GC.FIELD_TEL_GUID]: telGuid,
        [GC.FIELD_LOAD_GUID]: telGuid,
        [GC.FIELD_STOP_DROP]: G.isEventTypeDrop(type),
        [GC.FIELD_STOP_PICKUP]: G.isEventTypePickup(type),
        [GC.FIELD_LOCATION]: R.pathOr({}, [locationTemplateGuid], locations),
      },
    ]);

    const newStop = createNewTerminalOnRouteTel(options);

    return R.assoc(GC.FIELD_STOP_ID, G.generateGuid(), newStop);
  },
  R.pathOr([], [GC.FIELD_LOAD_STOPS], tel),
);

const getFirstEventWithInitDates = ({ lateDate, earlyDate, templateTels }) => {
  const firstEvent = R.compose(
    R.head,
    R.sort((prevEvent, event) => (
      G.getDateRange(G.getStopEarlyDate(event), G.getStopEarlyDate(prevEvent))
    )),
    R.flatten,
    R.map(R.prop(GC.FIELD_LOAD_STOPS)),
  )(templateTels);

  return R.mergeRight(firstEvent, {
    initLateDate: lateDate,
    initEarlyDate: earlyDate,
  });
};

export const getCurrentRouteWithRouteTemplateEvents = ({
  tel,
  locations,
  currentRoute,
  routeTemplate,
  currentBranchGuid,
  firstEventDates: { lateDate, earlyDate },
}) => {
  const { guid, isNew, events } = tel;

  const templateTels = R.pathOr([], [GC.SYSTEM_LIST_TELS], routeTemplate);
  const currentRouteTels = R.pathOr([], [GC.SYSTEM_LIST_TELS], currentRoute);

  const order = R.length(R.values(currentRouteTels));

  const shouldUpdateCurrentTel = G.isAllTrue(isNew, R.equals(1, order), G.isNilOrEmpty(events));

  const routeTemplateTels = G.ifElse(
    shouldUpdateCurrentTel,
    R.tail(templateTels),
    templateTels,
  );

  const firstEventWithInitDates = getFirstEventWithInitDates({ lateDate, earlyDate, templateTels });

  const newTels = routeTemplateTels.map((tel, i) => {
    const telGuid = G.generateGuid();

    const newEvents = getNewTelEventsFromRouteTemplate({
      tel,
      telGuid,
      locations,
      firstEventWithInitDates,
    });

    const telInfo = {
      order: R.add(order, i),
      [GC.FIELD_GUID]: telGuid,
      [GC.FIELD_BRANCH_GUID]: currentBranchGuid,
      [GC.FIELD_LOAD_STOPS]: sortTelEventsByDate(newEvents),
      [GC.FIELD_PRIMARY_REFERENCE]: { value: `Tel-${R.add(R.inc(order), i)}` },
    };

    return createNewRouteTel(telInfo);
  });

  let routeTels = currentRouteTels;

  if (shouldUpdateCurrentTel) {
    const currentTelEvents = getNewTelEventsFromRouteTemplate({
      locations,
      firstEventWithInitDates,
      [GC.FIELD_TEL_GUID]: guid,
      [GC.FIELD_TEL]: R.head(templateTels),
    });

    const currentTelWithNewEvents = R.assoc(GC.FIELD_LOAD_STOPS, sortTelEventsByDate(currentTelEvents), tel);

    routeTels = R.assocPath([guid], currentTelWithNewEvents, currentRouteTels);
  }

  const newCurrentRouteTels = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.concat(R.values(routeTels)),
  )(newTels);

  return R.assoc(GC.SYSTEM_LIST_TELS, newCurrentRouteTels, currentRoute);
};

const getUpdatedEvents = ({
  tel,
  cloName,
  cloGuid,
  allItems,
  commonData,
  containers,
  stopsToSave,
  stopsToRemove,
  commonItemsData,
}) => R.compose(
  R.indexBy(R.prop(GC.FIELD_GUID)),
  R.map((event) => {
    if (R.and(G.isLoadTypeClo(event), G.notEquals(R.prop(GC.FIELD_CLO_GUID, event), cloGuid))) {
      return event;
    }

    const { droppedContainers, pickedUpContainers } = event;

    const items = R.compose(
      R.map(R.mergeRight(commonItemsData)),
      R.filter(G.isNotNil),
      R.map((item) => {
        if (R.not(G.isLoadTypeClo(item))) return item;

        const itemToSave = R.path([R.prop(GC.FIELD_ITEM_INTERNAL_ID, item)], allItems);

        if (G.isNotNil(itemToSave) && R.isNil(G.getGuidFromObject(itemToSave))) {
          return R.mergeRight(itemToSave, {
            isNew: true,
            [GC.FIELD_GUID]: G.generateGuid(),
          });
        }

        return itemToSave;
      }),
      R.prop(GC.FIELD_LOAD_ITEMS),
    )(event);

    return R.mergeRight(
      event,
      {
        [GC.FIELD_LOAD_ITEMS]: items,
        [GC.FIELD_STOP_ITEM_IDS]: R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), items),
        [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.map(
          R.mergeRight(commonItemsData),
          droppedContainers,
        ),
        [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.map(
          R.mergeRight(commonItemsData),
          pickedUpContainers,
        ),
      },
    );
  }),
  R.values,
  R.omit(stopsToRemove),
  R.map((prevEvent) => {
    const eventData = R.find(
      R.eqProps(GC.FIELD_GUID, prevEvent),
      stopsToSave,
    );

    if (G.isNotNil(eventData)) {
      const mergeData = R.assoc(
        GC.FIELD_LOAD_ITEMS,
        R.map(
          R.mergeRight({
            [C.CLO_NAME]: cloName,
            [GC.FIELD_LOAD_GUID]: cloGuid,
            [GC.FIELD_LOAD_TYPE]: GC.LOAD_TYPE_CLO,
          }),
          R.prop(GC.FIELD_LOAD_ITEMS, eventData),
        ),
        eventData,
      );

      return R.mergeAll([prevEvent, mergeData, commonData]);
    }

    const { pickedUpContainers, droppedContainers } = prevEvent;

    const getContainers = (entities) => R.compose(
      R.map((container) => R.propOr(container, R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, container), containers)),
      R.filter((container) => R.and(
        R.propEq(cloName, C.CLO_NAME, container),
        G.isNotNil(R.prop((R.prop(GC.FIELD_CONTAINER_INTERNAL_ID, container)), containers)),
      )),
    )(entities);

    const dropped = getContainers(droppedContainers);
    const picked = getContainers(pickedUpContainers);

    return {
      ...prevEvent,
      [GC.FIELD_STOP_DROPPED_CONTAINERS]: dropped,
      [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: picked,
      [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), dropped),
      [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID), picked),
    };
  }),
  R.prop(GC.FIELD_LOAD_STOPS),
)(tel);

export const updateCloStopsOnPlanner = (state, data) => {
  const { cloName, cloGuid, stopsData } = data;
  const { containers, stopsToSave, stopsToRemove } = stopsData;

  const commonItemsData = {
    [C.CLO_NAME]: cloName,
    [GC.FIELD_LOAD_GUID]: cloGuid,
    [GC.FIELD_LOAD_TYPE]: GC.LOAD_TYPE_CLO,
  };

  const commonData = {
    ...commonItemsData,
    [GC.FIELD_CLO_GUID]: cloGuid,
  };

  const editedCloEvents = R.map(
    (stop) => {
      const {
        items,
        droppedContainers,
        pickedUpContainers,
      } = stop;

      const stopData = {
        ...commonData,
        [GC.FIELD_LOAD_ITEMS]: R.map(
          R.mergeRight(commonItemsData),
          items,
        ),
        [GC.FIELD_STOP_DROPPED_CONTAINERS]: R.map(
          R.mergeRight(commonItemsData),
          droppedContainers,
        ),
        [GC.FIELD_STOP_PICKED_UP_CONTAINERS]: R.map(
          R.mergeRight(commonItemsData),
          pickedUpContainers,
        ),
      };

      if (R.isNil(R.path([GC.FIELD_GUID], stop))) {
        return R.mergeRight(stop, {
          ...stopData,
          isNew: true,
          [GC.FIELD_GUID]: G.generateGuid(),
        });
      }

      return R.mergeRight(stop, stopData);
    },
    stopsToSave,
  );

  const plannerEvents = R.reduce(
    R.mergeRight,
    {},
    R.map(
      R.prop(GC.FIELD_LOAD_STOPS),
      R.values(R.path(['currentRoute', GC.SYSTEM_LIST_TELS], state)),
    ),
  );

  const unassignedCloEvents = R.compose(
    R.uniqBy(R.prop(GC.FIELD_GUID)),
    R.concat(R.filter(
      (event) => R.not(R.propEq(cloGuid, GC.FIELD_CLO_GUID, event)),
      state.unassignedCloEvents,
    )),
    R.filter(
      ({ guid, isNew }) => {
        if (isNew) return true;

        return R.isNil(R.prop(guid, plannerEvents));
      },
    ),
  )(editedCloEvents);

  const terminalsWithoutEditedCloItems = R.compose(
    R.map((event) => R.assoc(
      GC.FIELD_STOP_ITEMS,
      R.filter(
        ({ loadGuid }) => G.notEquals(loadGuid, cloGuid),
        R.prop(GC.FIELD_STOP_ITEMS, event),
      ),
      event,
    )),
    R.filter((event) => R.not(G.isLoadTypeClo(event))),
    R.values,
    R.reduce(R.mergeRight, {}),
    R.map(R.prop(GC.FIELD_LOAD_STOPS)),
    R.values,
    R.path(['currentRoute', 'tels']),
  )(state);

  const notEditedCloEvents = R.compose(
    R.filter((event) => R.and(
      G.isLoadTypeClo(event),
      G.notEquals(R.prop(GC.FIELD_CLO_GUID, event), cloGuid),
    )),
    R.values,
  )(plannerEvents);

  const eventsWithAllItems = [...terminalsWithoutEditedCloItems, ...stopsToSave, ...notEditedCloEvents];

  const allItems = R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID), R.reduce(
    R.concat,
    [],
    R.map(R.prop(GC.FIELD_STOP_ITEMS), eventsWithAllItems),
  ));

  const newTels = R.compose(
    R.map((tel) => {
      const updatedEvents = getUpdatedEvents({
        tel,
        cloName,
        cloGuid,
        allItems,
        commonData,
        containers,
        stopsToSave,
        stopsToRemove,
        commonItemsData,
      });

      return R.assoc(GC.FIELD_LOAD_STOPS, updatedEvents, tel);
    }),
    R.path(['currentRoute', GC.SYSTEM_LIST_TELS]),
  )(state);

  return P.$all(
    P.$set('currentRoute.tels', newTels),
    P.$set('deletedEventGuids', stopsToRemove),
    P.$set(`editedClos.${cloGuid}`, editedCloEvents),
    P.$set('unassignedCloEvents', unassignedCloEvents),
    P.$set('currentRouteItems', refreshRouteItems(newTels)),
    P.$set('currentRouteContainers', refreshRouteContainers(newTels)),
    state,
  );
};

export const uncheckAllItemsAndRemoveDetails = R.map(R.compose(
  R.dissoc('details'),
  R.assoc('expanded', false),
  R.assoc('selected', false),
));

export const uncheckAllItemsRemoveDetailsAndRemovePlannedEvents = (list, hidePlanned) => {
  const newList = uncheckAllItemsAndRemoveDetails(list);

  if (R.not(hidePlanned)) return newList;

  return R.filter(
    ({ telGuid }) => R.isNil(telGuid),
    newList,
  );
};

export const getCloEventListWithUpdatedStop = (list, updatedStop) => R.map(
  (stop) => {
    if (R.eqProps(GC.FIELD_GUID, stop, updatedStop)) {
      return R.mergeRight(stop, updatedStop);
    }

    return stop;
  },
  list,
);
// REDUCER

// SELECTORS
export const getRouteTotalDistance = (loads, distanceName) => {
  const loadsDistances = R.map(
    (load) => G.getLoadTotalDistance(load, distanceName),
    R.values(loads),
  );
  const totalTripDistance = R.sum(R.map(R.prop('totalTripDistance'), loadsDistances));
  const totalTripDistanceUom = G.ifElse(
    R.equals(NaN, totalTripDistance),
    '',
    R.pathOr(GC.UOM_MILE, [0, 'totalTripDistanceUom'], loadsDistances),
  );

  return {
    totalTripDistanceUom,
    totalTripDistance: G.ifElse(
      R.equals(NaN, totalTripDistance),
      'N/A',
      totalTripDistance.toFixed(2),
    ),
  };
};

export const getRouteClosOptions = (route, items) => {
  const closByEvents = R.compose(
    R.map((stop) => ({
      label: R.path([C.CLO_NAME], stop),
      value: R.prop(GC.FIELD_CLO_GUID, stop),
    })),
    R.uniqBy(R.prop(GC.FIELD_CLO_GUID)),
    R.filter(G.isLoadTypeClo),
    R.reduce(
      (acc, tel) => R.concat(
        acc,
        R.compose(
          R.values,
          R.pathOr({}, [GC.FIELD_LOAD_STOPS]),
        )(tel),
      ),
      [],
    ),
    R.values,
    R.pathOr({}, [GC.SYSTEM_LIST_TELS]),
  )(route);

  const closByItems = R.compose(
    R.map(({ CLO_NAME, loadGuid }) => ({
      label: CLO_NAME,
      value: loadGuid,
    })),
    R.uniqBy(R.prop(GC.FIELD_LOAD_GUID)),
    R.filter(G.isLoadTypeClo),
    R.values,
  )(items);

  return R.compose(
    R.uniqBy(R.prop(GC.FIELD_VALUE)),
    R.concat(closByItems),
  )(closByEvents);
};
// SELECTORS

// UI
export const getAvailableContainersForPickup = (stop, currentRoute) => {
  const path = [GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_TEMPLATE_ID];

  const terminalDropContainers = R.compose(
    R.indexBy(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_DROPPED_CONTAINERS, event)), []),
    R.filter((event) => R.and(
      R.or(R.pathEq(R.path(path, event), path, stop), R.eqProps(GC.FIELD_STOP_ID, event, stop)),
      G.isStopDrop(event),
    )),
    R.values,
    R.reduce((acc, tel) => R.mergeRight(acc, R.prop(GC.FIELD_LOAD_STOPS, tel)), {}),
    R.values,
    R.prop(GC.SYSTEM_LIST_TELS),
  )(currentRoute);

  const terminalPickedContainerIds = R.compose(
    R.filter(G.isNotNil),
    R.uniq,
    R.reduce((acc, event) => R.concat(acc, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, event))),
    R.filter((event) => R.and(
      R.or(R.pathEq(R.path(path, event), path, stop), R.eqProps(GC.FIELD_STOP_ID, event, stop)),
      G.isStopPickup(event),
    )),
    R.values,
    R.reduce((acc, tel) => R.mergeRight(acc, R.prop(GC.FIELD_LOAD_STOPS, tel))),
    R.values,
    R.prop(GC.SYSTEM_LIST_TELS),
  )(currentRoute);

  return R.compose(
    R.values,
    R.omit(R.concat(terminalPickedContainerIds, R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, stop))),
  )(terminalDropContainers);
};

const addContainersToTerminalPickupOrDrop = (currentRoute, stopId, drop) => R.assoc(
  GC.SYSTEM_LIST_TELS,
  R.compose(
    R.map((tel) => R.assoc(
      GC.FIELD_LOAD_STOPS,
      R.map(
        (stop) => {
          if (R.propEq(stopId, GC.FIELD_STOP_ID, stop)) {
            if (R.and(G.isStopDrop(stop), drop)) {
              const droppedContainers = getAvailableContainersForDrop(tel);

              return {
                ...stop,
                droppedContainers,
                [GC.FIELD_STOP_DROPPED_CONTAINER_IDS]: R.map(
                  R.prop(GC.FIELD_CONTAINER_INTERNAL_ID),
                  droppedContainers,
                ),
              };
            }

            if (R.and(G.isStopPickup(stop), R.not(drop))) {
              const pickedUpContainers = getAvailableContainersForPickup(stop, currentRoute);

              return {
                ...stop,
                pickedUpContainers,
                [GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS]: R.map(
                  R.prop(GC.FIELD_CONTAINER_INTERNAL_ID),
                  pickedUpContainers,
                ),
              };
            }

            return stop;
          }

          return stop;
        },
        tel.events,
      ),
      tel,
    )),
    R.propOr({}, GC.SYSTEM_LIST_TELS),
  )(currentRoute),
  currentRoute,
);

export const addContainersToRouteTerminal = (currentRoute, stopId) => {
  const routeWithDroppedContainers = addContainersToTerminalPickupOrDrop(currentRoute, stopId, true);

  return addContainersToTerminalPickupOrDrop(routeWithDroppedContainers, stopId, false);
};

export const getRelatedTelTotalInfo = (tel) => {
  if (G.isNilOrEmpty(tel[GC.FIELD_LOAD_STOPS])) return false;

  const items = R.uniq(R.reduce(
    R.concat,
    [],
    R.map(
      G.getPropFromObject(GC.FIELD_LOAD_ITEMS),
      R.values(tel[GC.FIELD_LOAD_STOPS]),
    ),
  ));

  if (G.isNilOrEmpty(items)) return false;

  return {
    itemsTotal: items.length,
    totalQuantity: G.calculateTotalQuantity(items),
    totalWeight: G.calcItemsTotalWeightWithoutQty(items),
    totalDistance: G.getLoadTotalDistance(tel, 'telDistanceToNextStop'),
  };
};

export const getStopDistanceInfo = (manual, distance) => {
  if (G.isNotNil(manual)) {
    return `
      ${R.pathOr('', [GC.FIELD_DISTANCE_MANUAL], distance)}
      ${R.pathOr('', [GC.FIELD_DISTANCE_MANUAL_UOM], distance)}
    `;
  }

  return `
    ${R.pathOr('', [GC.FIELD_DISTANCE_SYSTEM], distance)}
    ${R.pathOr('', [GC.FIELD_DISTANCE_SYSTEM_UOM], distance)}
  `;
};

export const getStopItemsCount = (stop={}) => {
  if (R.or(
    G.isTrue(R.path(['cloEvent'], stop)),
    G.notEquals(R.path(['stopType'], stop), GC.STOP_TYPE_TERMINAL),
  )) {
    return R.length(stop.itemIds);
  }

  return R.compose(
    R.add(R.length(stop.items)),
    R.length,
    R.filter(
      (id) => G.isNilOrEmpty(R.find(
        ({ itemInternalId }) => R.equals(itemInternalId, id),
        stop.items,
      )),
    ),
  )(stop.itemIds);
};

export const checkTelExpandedOnPlanner = (stops, mode, visibility) => {
  if (G.isNotNilAndNotEmpty(visibility)) {
    return G.ifElse(R.equals(visibility, 'visible'), true, false);
  }

  if (R.equals(mode, C.PLANNER_TEL_MODES.ALL_EXPANDED)) return true;

  if (R.equals(mode, C.PLANNER_TEL_MODES.ALL_COLLAPSED)) return false;

  const typeMap = {
    [C.PLANNER_TEL_MODES.INBOUND]: GC.EVENT_TYPE_DROP,
    [C.PLANNER_TEL_MODES.OUTBOUND]: GC.EVENT_TYPE_PICKUP,
  };
  const terminals = R.filter(
    (stop) => R.equals(stop.stopType, GC.STOP_TYPE_TERMINAL),
    stops,
  );

  return R.all(
    R.propEq(typeMap[mode], GC.FIELD_EVENT_TYPE),
    terminals,
  );
};

export const createTelOptions = (currentRoute) => R.compose(
  R.map((tel) => ({ label: getLoadName(tel, 'new tel'), value: G.getGuidFromObject(tel) })),
  R.values,
  R.propOr({}, GC.SYSTEM_LIST_TELS),
)(currentRoute);

export const isInPlanner = (currentRoute, { itemIds, droppedContainerIds }) => R.compose(
  R.any(G.isTrue),
  R.map((tel) => {
    const events = R.compose(
      R.filter((stop) => G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop))),
      R.values,
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tel);

    const pickedTerminalItems = R.map(
      R.prop(GC.FIELD_ITEM_INTERNAL_ID),
      getTelCloItemsByType({ events }, GC.EVENT_TYPE_PICKUP),
    );

    const droppedTerminalItems = R.map(
      R.prop(GC.FIELD_ITEM_INTERNAL_ID),
      getTelCloItemsByType({ events }, GC.EVENT_TYPE_DROP),
    );

    const picked = R.includes(R.head(itemIds), pickedTerminalItems);
    const dropped = R.includes(R.head(itemIds), droppedTerminalItems);

    const pickedContainers = R.any(
      (stop) => R.and(
        G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop)),
        R.includes(R.head(droppedContainerIds), R.prop(GC.FIELD_STOP_PICKED_UP_CONTAINER_IDS, stop)),
      ),
      events,
    );

    const droppedContainers = R.any(
      (stop) => R.and(
        G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop)),
        R.includes(R.head(droppedContainerIds), R.prop(GC.FIELD_STOP_DROPPED_CONTAINER_IDS, stop)),
      ),
      events,
    );

    return R.or(R.and(picked, dropped), R.and(pickedContainers, droppedContainers));
  }),
  R.values,
  R.prop(GC.SYSTEM_LIST_TELS),
)(currentRoute);

export const getCurrentRouteCloEventGuids = R.compose(
  R.map(G.getGuidFromObject),
  R.filter(G.isLoadTypeClo),
    R.reduce(
      (acc, tel) => R.concat(
        acc,
        R.compose(
          R.values,
          R.pathOr({}, [GC.FIELD_LOAD_STOPS]),
        )(tel),
      ),
      [],
    ),
    R.values,
    R.pathOr({}, [GC.SYSTEM_LIST_TELS]),
);

export const getTelsWithTerminalEvents = (tels, locations) => R.map(
  (tel) => {
    const events = R.compose(
      R.map((event) => {
        const { location, eventType, eventLateDate, eventEarlyDate } = event;

        const { templateId, locationName } = location;

        return {
          locationName,
          eventLateDate,
          eventEarlyDate,
          type: eventType,
          locationTemplateGuid: R.path([templateId, GC.FIELD_GUID], locations),
        };
      }),
      R.filter(({ stopType }) => G.isStopTypeTerminal(stopType)),
      R.values,
      R.pathOr([], [GC.FIELD_LOAD_STOPS]),
    )(tel);

    return { events };
  },
  tels,
);

export const getRouteTemplateOptions = (routeTemplates) => R.map(({ guid, name, tels }) => {
  const events = R.compose(
    R.sort((prevEvent, event) => (
      G.getDateRange(G.getStopEarlyDate(event), G.getStopEarlyDate(prevEvent))
    )),
    R.uniqBy(R.prop(GC.FIELD_LOCATION_NAME)),
    R.flatten,
    R.map(R.prop(GC.FIELD_LOAD_STOPS)),
  )(tels);

  const eventLocationNames = R.compose(
    R.join(' - '),
    R.map(R.prop(GC.FIELD_LOCATION_NAME)),
  )(events);

  return ({
    [GC.FIELD_VALUE]: guid,
    [GC.FIELD_LABEL]: `${name} (${eventLocationNames})`,
  });
}, routeTemplates);

// UI

// logic
const isWrongEventDate = (event, index, events) => {
  const eventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_DATE]),
      G.isNotNilAndNotEmpty(event[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]),
    ),
    `${event[GC.FIELD_LOAD_APPOINTMENT_DATE]} ${event[GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]}`,
    null,
  );

  const prevEventAppointment = G.ifElse(
    R.and(
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events)),
      G.isNotNilAndNotEmpty(R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], events)),
    ),
    `${R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_DATE], events)} ${
      R.path([R.dec(index), GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], events)}`,
    null,
  );

  return R.and(
    R.gt(index, 0),
    G.isBeforeDate(
      R.or(eventAppointment, event[GC.FIELD_LOAD_EVENT_LATE_DATE]),
      R.or(prevEventAppointment, R.path([R.dec(index), GC.FIELD_LOAD_EVENT_EARLY_DATE], events)),
    ),
  );
};

const getFilteredItems = (event, items) => R.filter(
  (item) => G.isNilOrEmpty(R.find(
    R.eqProps(GC.FIELD_ITEM_INTERNAL_ID, item),
    items,
  )),
  R.pathOr([], [GC.FIELD_STOP_ITEMS], event),
);

const getWrongPickupItems = (event, index, events) => {
  const dropItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_STOP_ITEMS)),
    R.filter(R.propEq(GC.EVENT_TYPE_DROP, GC.FIELD_EVENT_TYPE)),
    R.slice(R.inc(index), Infinity),
  )(events);

  return getFilteredItems(event, dropItems);
};

const getWrongDropItems = (event, index, events) => {
  const pickupItems = R.compose(
    R.reduce(R.concat, []),
    R.map(R.prop(GC.FIELD_STOP_ITEMS)),
    R.filter(R.propEq(GC.EVENT_TYPE_PICKUP, GC.FIELD_EVENT_TYPE)),
    R.take(index),
  )(events);

  return getFilteredItems(event, pickupItems);
};

export const getEventsErrors = (events) => {
  let errors = {};

  events.forEach((event, index) => {
    const isPickup = G.isEventTypePickup(R.prop(GC.FIELD_EVENT_TYPE, event));
    const isWrongDate = isWrongEventDate(event, index, events);

    if (isWrongDate) {
      errors = R.assocPath([R.prop(GC.FIELD_GUID, event), GC.FIELD_DATE], true, errors);
    }

    const wrongItems = G.ifElse(
      isPickup,
      getWrongPickupItems,
      getWrongDropItems,
    )(event, index, events);

    if (G.isNotEmpty(wrongItems)) {
      errors = R.assocPath([R.prop(GC.FIELD_GUID, event), GC.FIELD_LOAD_ITEMS], wrongItems, errors);
    }
  });

  return errors;
};

export const getItemsMergeData = ({ cloName, eventItems, matchEventItems }) => {
  const matchEventItemsWithCloGuid = R.map((matchEventItem) => {
    if (R.and(G.isNotNil(cloName), G.isLoadTypeClo(matchEventItem))) {
      return R.assoc(C.CLO_NAME, cloName, matchEventItem);
    }

    return matchEventItem;
  }, matchEventItems);

  const newEventItems = R.compose(
    R.uniqBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
    R.concat(matchEventItemsWithCloGuid),
  )(eventItems);

  return {
    [GC.FIELD_STOP_ITEMS]: newEventItems,
    [GC.FIELD_STOP_ITEM_IDS]: R.map(R.prop(GC.FIELD_ITEM_INTERNAL_ID), newEventItems),
  };
};

const getUpdatedItems = (currentItems, newItems) => R.compose(
  R.indexBy(R.prop(GC.FIELD_ITEM_INTERNAL_ID)),
  R.map((currentItem) => {
    const itemId = G.getPropFromObject(GC.FIELD_ITEM_INTERNAL_ID, currentItem);
    const matchItem = G.getPropFromObject(itemId, newItems);

    if (G.isNotNil(matchItem) && R.not(R.eqProps(GC.FIELD_VERSION, matchItem, currentItem))) {
      let fieldsFromCurrentItem = ['isNew', GC.FIELD_SELECTED];

      if (G.isLoadTypeClo(matchItem)) {
        fieldsFromCurrentItem = R.append(C.CLO_NAME, fieldsFromCurrentItem);
      }

      return R.mergeRight(matchItem, R.pick(fieldsFromCurrentItem, currentItem));
    }

    return currentItem;
  }),
)(currentItems);

const getAddedItems = ({ addedItemIds, cloEventList, itemsFromEvents }) => {
  if (G.isNilOrEmpty(addedItemIds)) return {};

  return R.map((item) => {
    let mergeData = { isNew: false, [GC.FIELD_SELECTED]: true };

    if (G.isLoadTypeClo(item)) {
      const { loadGuid } = item;

      const event = R.find(R.propEq(loadGuid, GC.FIELD_CLO_GUID), cloEventList);

      mergeData = R.assoc(C.CLO_NAME, G.getPropFromObject(GC.GRC.CLO_PRIMARY_REFERENCE_VALUE, event), mergeData);
    }

    return R.mergeRight(item, mergeData);
  }, R.pick(addedItemIds, itemsFromEvents));
};

export const getCurrentRouteItems = ({
  cloEventList,
  itemsFromEvents,
  currentRouteItems,
  deletedRouteItemIds,
}) => {
  const currentItemsWithoutDeleted = R.compose(
    R.values,
    R.omit(deletedRouteItemIds),
  )(currentRouteItems);

  const updatedRouteItems = getUpdatedItems(currentItemsWithoutDeleted, itemsFromEvents);

  const addedRouteItems = getAddedItems({
    cloEventList,
    itemsFromEvents,
    addedItemIds: R.difference(R.keys(itemsFromEvents), R.keys(currentRouteItems)),
  });

  return R.mergeRight(updatedRouteItems, addedRouteItems);
};

export const getMaxTelOrder = (tels) => R.compose(
  R.head,
  R.sort((prevOrder, order) => R.subtract(order, prevOrder)),
  R.map(R.prop(GC.FIELD_ORDER)),
  R.values,
)(tels);
// logic

export const getTelTotalInfo = (tel, items) => {
  if (G.isNilOrEmpty(tel[GC.FIELD_LOAD_STOPS])) return false;

  const itemIds = R.uniq(R.reduce(
    R.concat,
    [],
    R.map(
      (event) => event.itemIds,
      R.values(tel[GC.FIELD_LOAD_STOPS]),
    ),
  ));

  const telItems = R.filter(
    (item) => G.isNotNilAndNotEmpty(item),
    R.map(
      (id) => R.prop(id, items),
      itemIds,
    ),
  );

  if (G.isNilOrEmpty(telItems)) return false;

  return {
    itemsTotal: telItems.length,
    totalQuantity: G.calculateTotalQuantity(telItems),
    palletSpaces: G.calculateItemsPalletSpaces(telItems),
    totalWeight: G.calcItemsTotalWeightWithoutQty(telItems, false),
    totalDistance: G.getLoadTotalDistance(tel, 'telDistanceToNextStop'),
    totalBillableWeight: R.any(R.prop(GC.FIELD_BILLABLE_WEIGHT), telItems)
      ? G.calcItemsTotalWeightWithoutQty(telItems)
      : {},
  };
};


export const getAvailableListHeight = ({
  plannerView,
  renderedListHeight,
}) => {
  if (R.includes(plannerView, [C.COLLAPSED, C.MAP_MODE, C.STICKED_RIGHT])) {
    return Math.max(0, renderedListHeight - C.TOOLBAR_HEIGHT);
  }

  return Math.max(0, renderedListHeight);
};

const normalizePlannerUISettings = (settings) => {
  if (G.notEquals(R.type(settings), 'Object')) return {};

  const plannerView = R.prop('plannerView', settings);
  const paneSizes = R.pick(['mapWidth', 'contentOneSize', 'stickToRightWidth'], settings);

  const isValidPlannerView = R.includes(plannerView, [
    C.COLLAPSED,
    C.HALF_SCREEN,
    C.FULL_SCREEN,
    C.MAP_MODE,
    C.STICKED_RIGHT,
  ]);

  const validPaneSizes = R.pickBy(
    (size) => G.isNumber(size) && Number.isFinite(size) && size > 0,
    paneSizes,
  );

  return R.pickBy(G.isNotNil, {
    ...validPaneSizes,
    plannerView: isValidPlannerView ? plannerView : null,
  });
};

export const getPlannerUISettings = () => {
  try {
    const settings = JSON.parse(window.localStorage.getItem(C.PLANNER_UI_SETTINGS_STORAGE_KEY));

    return normalizePlannerUISettings(settings);
  } catch (error) { // eslint-disable-line no-unused-vars
    return {};
  }
};

export const getInitialPlannerView = (settings) => R.propOr(
  C.HALF_SCREEN,
  'plannerView',
  settings,
);

export const updatePlannerUISettings = (settings) => {
  const currentSettings = getPlannerUISettings();
  const nextSettings = normalizePlannerUISettings(R.mergeRight(currentSettings, settings));

  try {
    window.localStorage.setItem(
      C.PLANNER_UI_SETTINGS_STORAGE_KEY,
      JSON.stringify(nextSettings),
    );
  } catch (error) { // eslint-disable-line no-unused-vars
    return false;
  }

  return true;
};

export const getPlannerSplitSizeKey = (plannerView) => ({
  [C.MAP_MODE]: 'mapWidth',
  [C.HALF_SCREEN]: 'contentOneSize',
  [C.STICKED_RIGHT]: 'stickToRightWidth',
}[plannerView]);

export const updatePlannerSplitSize = (plannerView, contentOneSize) => {
  const sizeKey = getPlannerSplitSizeKey(plannerView);

  if (!sizeKey || !Number.isFinite(contentOneSize) || contentOneSize <= 0) return false;

  return updatePlannerUISettings({ [sizeKey]: contentOneSize });
};

export {
  addTelItems,
  sortTelEvents,
  getItemsToRemove,
  getContainerInfo,
  getTableMaxHeight,
  concatArrayItemsProp,
  filterPickedDroppedItems,
  getLoadEditModeStopsGeodata,
  getAvailableContainersForDrop,
  indexObjectArrayPropByPropName,
  getAvailableContainersForCloDrop,
  getAvailableExistedTerminalOptions,
  getExistingTerminalStopIdByLocation,
  createContainerFromFormValuesAndStop,
} from '../dispatch-planner-v2/shared/helpers';
